import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError, fmtDate, API_BASE } from "../../lib/api";
import { maskCompetencia, competenciaFromInternal } from "../../lib/competencia";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import StatusDetalhe from "../../components/StatusDetalhe";
import { toast } from "sonner";
import {
  Briefcase, TrendingUp, AlertTriangle, CheckCircle2, PauseCircle, PlayCircle, Wallet,
  Plus, RotateCcw, Ban, Search, Percent, Tag, CalendarClock, Share2, Mail, MessageCircle,
  Barcode, History, X, CreditCard, RefreshCw, ExternalLink, Landmark, Star, Wifi, Copy, Pencil,
  QrCode, Settings, Eye, EyeOff, ShieldAlert, Filter, Hash,
} from "lucide-react";
import { PLATFORM_COMPANY_SHORT_NAME } from "../../lib/platformCompany";
import { isRealInstrumentMode, platformProviderLabel } from "../../lib/paymentProvider";

// Admin Master — Gestão Comercial SaaS: cobrança da ALTAFORJA/LOOTUS contra
// as EMPRESAS CLIENTES da plataforma. Domínio inteiramente separado do
// faturamento operacional dos tenants (fichas/boletos que cada empresa
// cliente emite para os PRÓPRIOS clientes) — nunca reaproveita essas telas/
// rotas. Catálogo de Planos/Recursos continua em Planos.jsx; aqui só a
// condição comercial de cada empresa e as cobranças SaaS em si.
const money = (v) => `R$ ${(Number(v) || 0).toFixed(2)}`;

// Pacote consolidado pós-homologação, Prioridade 9 — rótulos amigáveis
// para os campos seguros de before/after de um evento de histórico
// (o backend já garante que nunca há token/CNPJ completo/e-mail/payload
// bruto nesses dicts — ver audit_platform em server.py). Chave sem rótulo
// conhecido cai num fallback "Título Extraído da Chave", nunca escondida.
const HISTORY_FIELD_LABELS = {
  vencimento: "Vencimento", novo_vencimento: "Novo vencimento", anterior: "Vencimento anterior",
  multa_percentual: "Multa (%)", juros_mensal_percentual: "Juros ao mês (%)",
  multa_valor: "Multa", juros_valor: "Juros", dias_atraso: "Dias em atraso",
  valor_final: "Valor final", aplicar_encargos: "Aplicar encargos", motivo: "Motivo",
  status: "Situação", require_approval: "Exigir aprovação do supervisor", nome: "Nome",
  provider_order_id: "Identificador no Mercado Pago", tentativa: "Tentativa",
  instrumento_tipo: "Tipo de instrumento", aplicar_encargos_reemissao_default: "Aplicar encargos automaticamente",
};
const historyFieldLabel = (k) => HISTORY_FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const historyFieldValue = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Pacote consolidado pós-homologação, Prioridade 6 — padrão brasileiro
// "dd/mm/aaaa às HH:mm", nunca o formato bruto do backend nem "Invalid
// Date" quando o valor não é uma data/hora válida.
const fmtDateTimeBr = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()} às ${hh}:${min}`;
};

// Mercado Pago — Fase 1: rótulos/tons em PORTUGUÊS para o status interno
// (nunca a string bruta do Mercado Pago é mostrada ao usuário).
const SUBSCRIPTION_STATUS_LABEL = {
  pending: "Aguardando pagador", active: "Ativa", past_due: "Em atraso",
  paused: "Pausada", cancelled: "Cancelada", failed: "Falhou", not_started: "Não iniciada",
};
const SUBSCRIPTION_STATUS_TONE = {
  pending: "bg-blue-100 text-blue-800", active: "bg-emerald-100 text-emerald-800",
  past_due: "bg-red-100 text-red-800", paused: "bg-amber-100 text-amber-800",
  cancelled: "bg-slate-100 text-slate-600", failed: "bg-red-100 text-red-800",
  not_started: "bg-slate-100 text-slate-600",
};

// Mercado Pago — Fase 2 (Boleto SaaS via Orders API): mesma ideia, para o
// status interno do boleto (nunca a string bruta do Mercado Pago).
const BOLETO_STATUS_LABEL = {
  waiting_payment: "Aguardando pagamento", processing: "Processando pagamento", paid: "Pago",
  cancelled: "Cancelado", expired: "Vencido", failed: "Rejeitado", unknown: "Mercado Pago",
};
const BOLETO_STATUS_TONE = {
  waiting_payment: "bg-blue-100 text-blue-800", processing: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800", cancelled: "bg-slate-100 text-slate-600",
  expired: "bg-red-100 text-red-800", failed: "bg-red-100 text-red-800",
  unknown: "bg-slate-100 text-slate-600",
};

function StatCard({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`p-4 rounded-lg border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon size={14} /> {label}</div>
      <div className="font-heading text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// Fechamento da homologação, itens 2-4 — filtro "Empresa" do Faturamento
// SaaS. Reaproveita EXATAMENTE a mesma busca global já criada na rodada
// anterior (GET /admin/commercial/companies?q=..., resolvida no servidor —
// razão social, fantasia, CPF/CNPJ, telefone com/sem máscara, responsável,
// e-mail, id) em vez de duplicar uma segunda implementação de busca.
// Combobox leve, sem dependência nova: digitar busca no servidor com
// debounce e mostra até 20 correspondências por vez (nunca a lista inteira
// de empresas de uma só vez).
function EmpresaFilterCombobox({ value, companies, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  const selected = companies.find(c => c.company_id === value);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/commercial/companies", { params: query ? { q: query } : {} });
        setResults(data.slice(0, 20));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open]);

  useEffect(() => {
    const onOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const pick = (companyId) => { onChange(companyId); setOpen(false); setQuery(""); };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          className="pl-7 pr-7"
          placeholder="Todas as empresas — pesquisar..."
          value={open ? query : (selected ? (selected.nome_fantasia || selected.razao_social) : "")}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setQuery(""); setOpen(true); }}
          data-testid="cobrancas-empresa-busca"
        />
        {value && !open && (
          <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => onChange("")} title="Limpar (todas as empresas)" data-testid="cobrancas-empresa-limpar">
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg">
          <button type="button" className="w-full text-left px-3 py-2 text-sm text-slate-500 hover:bg-slate-50" onClick={() => pick("")} data-testid="cobrancas-empresa-todas">
            Todas as empresas
          </button>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">Buscando...</div>}
          {!loading && query && results.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Nenhuma empresa encontrada</div>}
          {!loading && results.map(c => (
            <button key={c.company_id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => pick(c.company_id)} data-testid={`cobrancas-empresa-opt-${c.company_id}`}>
              <div className="font-medium text-slate-900">{c.nome_fantasia || c.razao_social}</div>
              {c.nome_fantasia && c.razao_social !== c.nome_fantasia && <div className="text-xs text-slate-400">{c.razao_social}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const CHARGE_STATUS_LABEL = { em_aberto: "Em aberto", pago: "Pago", vencido: "Vencido", cancelado: "Cancelado" };
// Contrato Permanente + Régua de Inadimplência — mesmos códigos/rótulos do
// backend (server.py: _DELINQUENCY_LADDER / overdue_notifications.tipo),
// nunca uma segunda classificação inventada no frontend.
const DELINQUENCY_STAGE_LABEL = {
  aviso_1: "1º aviso (D+1)", aviso_2: "2º aviso (D+5)", aviso_final: "Aviso final (D+9)",
  elegivel_suspensao: "Elegível para suspensão (D+10)", elegivel_rescisao: "Elegível para rescisão (D+30)",
  elegivel_negativacao: "Elegível para análise de negativação (D+60)",
};
const OVERDUE_TIPO_LABEL = { d1: "1º aviso", d5: "2º aviso", d9: "Aviso final" };
const DELINQUENCY_NEXT_ACTION_TONE = (etapa) => (
  etapa === "elegivel_suspensao" || etapa === "elegivel_rescisao" || etapa === "elegivel_negativacao"
    ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
);
// Pacote consolidado pós-homologação, Prioridade 8 — distinguir claramente
// "Pago" por baixa manual administrativa (baixa_origem === "manual") de
// "Pago" confirmado pelo provider (Mercado Pago, via sincronização/webhook
// — nunca marca baixa_origem nesse caso). Ambíguo antes: as duas telas
// mostravam só "Pago" ao lado do status do boleto ainda pendente, sem
// deixar claro qual dos dois picture está acontecendo.
// Configuração Central de E-mail de Cobrança — rótulos amigáveis para o
// campo "security" (mesmo vocabulário do backend: ssl_tls|starttls|none).
const SMTP_SECURITY_LABEL = { ssl_tls: "SSL/TLS", starttls: "STARTTLS", none: "Sem TLS" };

const chargeStatusBadge = (c) => {
  if (c.status === "em_aberto" && c.vencida) return <Badge className="bg-red-100 text-red-800">Vencida</Badge>;
  // Finalização do Faturamento Master, Parte A — "Aguardando emissão"
  // (título já existe, instrumento bancário ainda não) x "Em aberto"
  // (instrumento já gerado) é só de RÓTULO, reaproveita boleto_gerado.
  if (c.status === "em_aberto" && !c.boleto_gerado) return <Badge className="bg-slate-100 text-slate-600">Aguardando emissão</Badge>;
  if (c.status === "em_aberto") return <Badge className="bg-cyan-100 text-cyan-800">Em aberto</Badge>;
  if (c.status === "pago" && c.baixa_origem === "manual") {
    return <Badge className="bg-amber-100 text-amber-800" title={`Baixa manual administrativa — não confirmada pelo ${platformProviderLabel(c.boleto_modo) || "provedor"}`}>Pago — baixa manual</Badge>;
  }
  if (c.status === "pago") return <Badge className="bg-emerald-100 text-emerald-800" title={`Confirmado pelo ${platformProviderLabel(c.boleto_modo) || "provedor"}`}>Pago — confirmado pelo provider</Badge>;
  return <Badge className="bg-slate-200 text-slate-700">Cancelado</Badge>;
};

export default function GestaoComercial() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [overview, setOverview] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [companyFilters, setCompanyFilters] = useState({ q: "", plano_id: "", cobranca_ativa: "", vencidas_apenas: false });

  const [charges, setCharges] = useState([]);
  const [chargeFilters, setChargeFilters] = useState({ company_id: "", status: "", competencia: "", vencidas_apenas: false });

  // Contrato Permanente + Régua de Inadimplência (itens 14/15/16) —
  // painel dedicado, mesma fonte (saas_charges vencidas) do endpoint
  // GET /admin/commercial/inadimplencia; filtros aplicados no backend.
  const [inadimplencia, setInadimplencia] = useState({ indicadores: null, linhas: [] });
  const [inadFilters, setInadFilters] = useState({ q: "", plano_id: "", etapa: "", dias_min: "", dias_max: "", status_contratual: "" });
  // Finalização do Faturamento Master, Partes B/C — seleção em lote
  // (geração de boletos/PIX em massa + envio de e-mail em massa), sempre
  // sobre o resultado JÁ FILTRADO de `charges` (nunca "todas as cobranças
  // do banco" — "selecionar todos" respeita o filtro atual, item 6/25).
  const [selectedChargeIds, setSelectedChargeIds] = useState(() => new Set());
  const [batchJob, setBatchJob] = useState(null);
  const [batchStarting, setBatchStarting] = useState(false);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  // Histórico comercial (item 6) — aba PRÓPRIA, separada da condição
  // comercial vigente: plano, mensalidade, desconto, reajuste, vencimento,
  // suspensão, retomada, faturamento, pagamento, cancelamento.
  const [historyFilters, setHistoryFilters] = useState({ company_id: "" });
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyDetailEvent, setHistoryDetailEvent] = useState(null); // Prioridade 9 — detalhes seguros do evento

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailCharges, setDetailCharges] = useState([]);
  // Mercado Pago — Fase 1 (Assinatura Comercial SaaS)
  const [subscription, setSubscription] = useState(null);
  const [commercialForm, setCommercialForm] = useState({ data_adesao: "", valor_mensal: "", valor_adesao: "", observacao: "" });
  const [discountForm, setDiscountForm] = useState({ tipo: "", valor: "", inicio: "", termino: "", motivo: "" });
  const [adjustmentForm, setAdjustmentForm] = useState({ novo_valor: "", vigencia: "", motivo: "", observacao: "" });
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendForm, setSuspendForm] = useState({ motivo: "", retomada_prevista: "" });

  // Pacote consolidado pós-homologação, Prioridade 16 — "Liberar por 2
  // dias" (liberação de confiança, sempre 48h fixas, sempre com motivo).
  const [trustedAccessOpen, setTrustedAccessOpen] = useState(false);
  const [trustedAccessMotivo, setTrustedAccessMotivo] = useState("");

  // Suspensão por Inadimplência (delta 2026-09-15) — ação manual a partir
  // de D+10, sempre com motivo e confirmação explícita.
  const [suspendDelinquencyOpen, setSuspendDelinquencyOpen] = useState(false);
  const [suspendDelinquencyMotivo, setSuspendDelinquencyMotivo] = useState("");
  const [suspendingDelinquency, setSuspendingDelinquency] = useState(false);

  const [generateOpen, setGenerateOpen] = useState(false);
  const [generateForm, setGenerateForm] = useState({ company_id: "", competencia: "", force: false, override_motivo: "" });
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ data_pagamento: "", forma_pagamento: "", observacao: "" });
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoReason, setUndoReason] = useState("");
  const [dueDateTarget, setDueDateTarget] = useState(null);
  const [dueDateForm, setDueDateForm] = useState({ vencimento: "", motivo: "" });
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  // Mercado Pago — Fase 2 (Bancos da Plataforma)
  const [banks, setBanks] = useState([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [bankEditing, setBankEditing] = useState(null);
  const [bankForm, setBankForm] = useState({ nome: "", provider: "mercado_pago", environment: "sandbox", access_token: "", webhook_secret: "", active: true });
  const [bankTesting, setBankTesting] = useState(null);

  // PIX da Altaforja (PIX por chave) — Fase 3: QR estático gerado 100% pela
  // LOOTUS, exclusivo para cobrar a mensalidade das empresas clientes
  // (Financeiro/Gestão Comercial). Nunca é o cadastro "Bancos" de cima
  // (esse é só Mercado Pago) e nunca é a chave de um tenant (essa fica em
  // Configurações → Bancos e Pagamentos, de cada empresa).
  const [pixKeyAccounts, setPixKeyAccounts] = useState([]);
  const [pkaOpen, setPkaOpen] = useState(false);
  const [pkaEditing, setPkaEditing] = useState(null);
  const [pkaForm, setPkaForm] = useState({ name: "", institution_name: "", holder_name: "", key_type: "cpf", pix_key: "", active: true });
  const [pkaShowKey, setPkaShowKey] = useState(false);
  const [pkaRevealOpen, setPkaRevealOpen] = useState(null);
  const [pkaRevealing, setPkaRevealing] = useState(null);

  // Mercado Pago — Fase 2.3 (Configuração global de juros/multa + reemissão + PIX)
  const [billingConfig, setBillingConfig] = useState(null);
  const [billingConfigForm, setBillingConfigForm] = useState({ multa_percentual: "0", juros_mensal_percentual: "0", aplicar_encargos_reemissao_default: true, whatsapp_financeiro_numero: "" });

  // Configuração Central de E-mail de Cobrança — identidade e SMTP da
  // PRÓPRIA Codexpy (Admin Master → Configurações de cobrança → E-mail de
  // cobrança). smtp_password nunca vem preenchida do backend (só
  // password_configured) — o campo em branco na edição significa "manter
  // a senha já salva".
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailSettingsForm, setEmailSettingsForm] = useState({
    active: true, from_name: "", from_email: "", reply_to: "", smtp_host: "", smtp_port: "587",
    security: "ssl_tls", smtp_username: "", smtp_password: "",
  });
  const [emailSettingsEditing, setEmailSettingsEditing] = useState(false);
  const [testingEmailConnection, setTestingEmailConnection] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState("");
  const [reemitirTarget, setReemitirTarget] = useState(null);
  const [reemitirForm, setReemitirForm] = useState({ novo_vencimento: "", aplicar_encargos: true, motivo: "" });
  const [regularizarTarget, setRegularizarTarget] = useState(null); // { charge, tipo }
  const [regularizarForm, setRegularizarForm] = useState({ novo_vencimento: "", aplicar_encargos: true, motivo: "" });
  // BUG FINANCEIRO NA REGULARIZAÇÃO — a prévia de dias/multa/juros nunca é
  // mais calculada aqui no frontend (Math.floor/Math.round em float, com a
  // data local do navegador); vem sempre de GET .../preview-regularizacao,
  // a MESMA função usada pelo backend ao confirmar. data_referencia (a
  // data civil de São Paulo usada no cálculo) é reenviada na confirmação —
  // se o dia civil mudar entre abrir o modal e confirmar, o backend
  // rejeita com 409 em vez de cobrar um valor diferente do exibido.
  const [regularizarPreviewData, setRegularizarPreviewData] = useState(null);
  const [reemitirPreviewData, setReemitirPreviewData] = useState(null);
  const [pixModalCharge, setPixModalCharge] = useState(null);
  const [instrumentConfirm, setInstrumentConfirm] = useState(null); // { charge, tipo } — confirmação de substituição boleto<->PIX

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/admin/commercial/overview"); setOverview(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  const loadPlans = useCallback(async () => {
    try { const { data } = await api.get("/admin/plans"); setPlans(data); } catch { /* noop */ }
  }, []);
  const loadCompanies = useCallback(async () => {
    try {
      const params = { ...companyFilters };
      if (params.cobranca_ativa === "") delete params.cobranca_ativa;
      const { data } = await api.get("/admin/commercial/companies", { params });
      setCompanies(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [companyFilters]);
  const loadCharges = useCallback(async () => {
    try { const { data } = await api.get("/admin/commercial/charges", { params: chargeFilters }); setCharges(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [chargeFilters]);
  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/admin/commercial/history", { params: historyFilters }); setHistoryEvents(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [historyFilters]);
  const loadInadimplencia = useCallback(async () => {
    try {
      const params = { ...inadFilters };
      if (params.dias_min === "") delete params.dias_min;
      if (params.dias_max === "") delete params.dias_max;
      const { data } = await api.get("/admin/commercial/inadimplencia", { params });
      setInadimplencia(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [inadFilters]);
  const loadBanks = useCallback(async () => {
    try { const { data } = await api.get("/admin/commercial/banks"); setBanks(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  const loadPixKeyAccounts = useCallback(async () => {
    try { const { data } = await api.get("/admin/commercial/pix-key-accounts"); setPixKeyAccounts(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  const loadBillingConfig = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/commercial/billing-config");
      setBillingConfig(data);
      setBillingConfigForm({
        multa_percentual: String(data.multa_percentual), juros_mensal_percentual: String(data.juros_mensal_percentual),
        aplicar_encargos_reemissao_default: data.aplicar_encargos_reemissao_default,
        whatsapp_financeiro_numero: data.whatsapp_financeiro_numero || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadEmailSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/commercial/email-settings");
      setEmailSettings(data);
      // smtp_password NUNCA vem do backend — o campo do form fica sempre
      // vazio ao (re)carregar (item 23: em branco = "manter a senha já
      // configurada"), preenchido só se o Admin Master digitar uma nova.
      setEmailSettingsForm({
        active: data.active, from_name: data.from_name || "", from_email: data.from_email || "",
        reply_to: data.reply_to || "", smtp_host: data.smtp_host || "", smtp_port: String(data.smtp_port || 587),
        security: data.security || "ssl_tls", smtp_username: data.smtp_username || "", smtp_password: "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { loadOverview(); loadPlans(); }, [loadOverview, loadPlans]);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadCharges(); }, [loadCharges]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadInadimplencia(); }, [loadInadimplencia]);
  useEffect(() => { loadBanks(); }, [loadBanks]);
  useEffect(() => { loadPixKeyAccounts(); }, [loadPixKeyAccounts]);
  useEffect(() => { loadBillingConfig(); }, [loadBillingConfig]);
  useEffect(() => { loadEmailSettings(); }, [loadEmailSettings]);

  // Lote — a seleção nunca sobrevive a uma troca de filtro (evita "gerar
  // boleto" para uma linha que nem está mais visível).
  useEffect(() => { setSelectedChargeIds(new Set()); }, [chargeFilters]);

  const toggleSelectCharge = (id) => {
    setSelectedChargeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  // Item 10 — a visualização PADRÃO da tela de Faturamento SaaS não pode
  // ficar poluída com histórico cancelado (carteira de milhares de
  // empresas misturada com tentativas antigas); reaparece só quando o
  // próprio filtro de Status escolhido for "Cancelado" (ou qualquer outro
  // filtro explícito — aí a lista já vem exatamente como pedida do
  // backend, sem filtragem extra aqui). Histórico nunca é apagado — só
  // não é a visão padrão.
  const visibleCharges = chargeFilters.status ? charges : charges.filter(c => c.status !== "cancelado");
  const batchSelectableIds = visibleCharges.filter(c => c.status !== "cancelado").map(c => c.id);
  const allSelected = batchSelectableIds.length > 0 && batchSelectableIds.every(id => selectedChargeIds.has(id));
  const toggleSelectAll = () => {
    setSelectedChargeIds(prev => {
      if (allSelected) return new Set();
      return new Set(batchSelectableIds);
    });
  };

  // Polling do progresso do lote (item 9) — nunca bloqueia a tela, para
  // sozinho quando o lote conclui.
  useEffect(() => {
    if (!batchJob || batchJob.status === "concluido") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/commercial/saas-billing/batch/${batchJob.id}`);
        setBatchJob(data);
        if (data.status === "concluido") { loadCharges(); loadHistory(); }
      } catch (e) { /* falha transitória de poll não derruba a tela — tenta de novo no próximo ciclo */ }
    }, 2000);
    return () => clearInterval(t);
  }, [batchJob, loadCharges, loadHistory]);

  const startBatchGenerate = async () => {
    if (selectedChargeIds.size === 0) return;
    setBatchStarting(true);
    try {
      const { data } = await api.post("/admin/commercial/saas-billing/batch/generate-boletos", { charge_ids: Array.from(selectedChargeIds) });
      setBatchJob(data);
      setSelectedChargeIds(new Set());
      toast.success(`Lote iniciado — ${data.total} cobrança(s) na fila`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBatchStarting(false); }
  };
  const startBatchEmail = async (reenviar = false) => {
    if (selectedChargeIds.size === 0) return;
    setBatchStarting(true);
    try {
      const { data } = await api.post("/admin/commercial/saas-billing/batch/send-emails", { charge_ids: Array.from(selectedChargeIds), reenviar });
      setBatchJob(data);
      setSelectedChargeIds(new Set());
      toast.success(reenviar ? `Reenvio iniciado — ${data.total} e-mail(is) na fila` : `Envio em lote iniciado — ${data.total} e-mail(is) na fila`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBatchStarting(false); }
  };
  const BATCH_ITEM_STATUS_LABEL = {
    pendente: "Na fila", em_processamento: "Processando", sucesso: "Boletos emitidos",
    ja_possuia_cobranca: "Já tinha cobrança", falha_cadastral: "Falha de cadastro",
    falha_provedor: "Falha no provedor", enviado: "E-mails enviados", falha_envio: "Falha no envio",
    ainda_nao_elegivel: "Ainda não disponível (D-10)", ja_enviado: "Já enviado (pulado)",
  };

  const loadDetail = async (cid) => {
    try {
      const { data } = await api.get(`/admin/companies/${cid}/commercial`);
      setDetail(data);
      setCommercialForm({
        data_adesao: (data.term.data_adesao || "").slice(0, 10),
        valor_mensal: data.term.valor_mensal ?? "", valor_adesao: data.term.valor_adesao ?? "",
        observacao: data.term.observacao || "",
      });
      setDiscountForm(data.term.desconto
        ? { ...data.term.desconto } : { tipo: "", valor: "", inicio: "", termino: "", motivo: "" });
      setAdjustmentForm(data.term.reajuste_programado
        ? { ...data.term.reajuste_programado } : { novo_valor: "", vigencia: "", motivo: "", observacao: "" });
      const { data: chargesData } = await api.get("/admin/commercial/charges", { params: { company_id: cid } });
      setDetailCharges(chargesData);
      const { data: subData } = await api.get(`/admin/commercial/companies/${cid}/subscription`);
      setSubscription(subData);
    } catch (e) { toast.error(formatApiError(e)); setDetail(null); }
  };
  const openDetail = (cid) => { setDetailId(cid); loadDetail(cid); };
  const closeDetail = () => {
    setDetailId(null); setDetail(null);
    if (searchParams.get("empresa")) { searchParams.delete("empresa"); setSearchParams(searchParams); }
  };

  useEffect(() => {
    const eid = searchParams.get("empresa");
    if (eid) openDetail(eid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDetail = () => { if (detailId) loadDetail(detailId); loadOverview(); loadCompanies(); loadCharges(); loadHistory(); };

  const saveCommercial = async () => {
    try {
      await api.put(`/admin/companies/${detailId}/commercial`, {
        data_adesao: commercialForm.data_adesao || null,
        valor_mensal: commercialForm.valor_mensal === "" ? null : Number(commercialForm.valor_mensal),
        valor_adesao: commercialForm.valor_adesao === "" ? null : Number(commercialForm.valor_adesao),
        observacao: commercialForm.observacao,
      });
      toast.success("Condição comercial atualizada");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveDiscount = async () => {
    try {
      const body = discountForm.tipo
        ? { tipo: discountForm.tipo, valor: Number(discountForm.valor), inicio: discountForm.inicio, termino: discountForm.termino || null, motivo: discountForm.motivo }
        : { tipo: null };
      await api.put(`/admin/companies/${detailId}/commercial/discount`, body);
      toast.success(discountForm.tipo ? "Desconto aplicado" : "Desconto removido");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearDiscount = async () => {
    try {
      await api.put(`/admin/companies/${detailId}/commercial/discount`, { tipo: null });
      toast.success("Desconto removido");
      setDiscountForm({ tipo: "", valor: "", inicio: "", termino: "", motivo: "" });
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveAdjustment = async () => {
    try {
      const body = adjustmentForm.novo_valor !== ""
        ? { novo_valor: Number(adjustmentForm.novo_valor), vigencia: adjustmentForm.vigencia, motivo: adjustmentForm.motivo, observacao: adjustmentForm.observacao }
        : { novo_valor: null };
      await api.put(`/admin/companies/${detailId}/commercial/adjustment`, body);
      toast.success("Reajuste programado");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const clearAdjustment = async () => {
    try {
      await api.put(`/admin/companies/${detailId}/commercial/adjustment`, { novo_valor: null });
      toast.success("Reajuste cancelado");
      setAdjustmentForm({ novo_valor: "", vigencia: "", motivo: "", observacao: "" });
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitSuspend = async () => {
    if (!suspendForm.motivo.trim()) return toast.error("Informe o motivo da suspensão");
    try {
      await api.post(`/admin/companies/${detailId}/commercial/suspend`, suspendForm);
      toast.success("Cobrança suspensa");
      setSuspendOpen(false); setSuspendForm({ motivo: "", retomada_prevista: "" });
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resumeBilling = async () => {
    try {
      await api.post(`/admin/companies/${detailId}/commercial/resume`);
      toast.success("Cobrança retomada");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Pacote consolidado pós-homologação, Prioridade 16 — liberação de
  // confiança: sempre 48h fixas (definidas pelo backend, nunca escolhidas
  // aqui) e sempre com motivo. Recalcula/desbloqueia imediatamente.
  const grantTrustedAccess = async () => {
    if (!trustedAccessMotivo.trim()) return toast.error("Informe o motivo da liberação por confiança");
    try {
      await api.post(`/admin/commercial/companies/${detailId}/trusted-access`, { motivo: trustedAccessMotivo.trim() });
      toast.success("Acesso liberado por 48h");
      setTrustedAccessOpen(false); setTrustedAccessMotivo("");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revokeTrustedAccess = async () => {
    try {
      await api.delete(`/admin/commercial/companies/${detailId}/trusted-access`);
      toast.success("Liberação de confiança revogada");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Suspensão por Inadimplência — reutiliza o mesmo mecanismo de bloqueio
  // financeiro (backend revalida elegibilidade D+10 e cobrança ainda
  // vencida antes de aplicar; nunca confia no que esta tela mostra).
  const suspendDelinquency = async () => {
    if (!suspendDelinquencyMotivo.trim()) return toast.error("Informe o motivo da suspensão");
    setSuspendingDelinquency(true);
    try {
      await api.post(`/admin/commercial/companies/${detailId}/suspender-inadimplencia`, { motivo: suspendDelinquencyMotivo.trim() });
      toast.success("Empresa suspensa por inadimplência");
      setSuspendDelinquencyOpen(false); setSuspendDelinquencyMotivo("");
      refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSuspendingDelinquency(false); }
  };

  // Mercado Pago — Fase 1: o backend SEMPRE determina plano/valor (nunca o
  // frontend); estas ações só disparam a chamada, sem enviar nenhum preço.
  // As três rotas de ação devolvem o documento da assinatura "cru"; sempre
  // recarregamos via GET (mesma forma usada por loadDetail/`subscription`)
  // em vez de tentar remontar o formato aqui, para nunca dessincronizar.
  const reloadSubscription = async () => {
    const { data } = await api.get(`/admin/commercial/companies/${detailId}/subscription`);
    setSubscription(data);
    return data;
  };
  const startSubscription = async () => {
    try {
      await api.post(`/admin/commercial/companies/${detailId}/subscription/start`, {});
      const data = await reloadSubscription();
      if (data?.subscription?.checkout_url) {
        toast.success("Assinatura iniciada — abra o link de pagamento para concluir");
        window.open(data.subscription.checkout_url, "_blank", "noopener,noreferrer");
      } else {
        toast.success("Assinatura Mercado Pago iniciada");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelSubscription = async () => {
    try {
      await api.post(`/admin/commercial/companies/${detailId}/subscription/cancel`);
      await reloadSubscription();
      toast.success("Assinatura Mercado Pago cancelada");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const syncSubscription = async () => {
    try {
      await api.post(`/admin/commercial/companies/${detailId}/subscription/sync`);
      await reloadSubscription();
      toast.success("Situação sincronizada com o Mercado Pago");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openGenerate = (companyId) => {
    setGenerateForm({ company_id: companyId || "", competencia: "", force: false, override_motivo: "" });
    setGenerateOpen(true);
  };
  // Item 15 — empresa com cobrança suspensa NUNCA é uma ação banal: a
  // confirmação forte + motivo obrigatório aparecem no próprio formulário
  // assim que uma empresa suspensa é selecionada (o backend também valida
  // os dois de novo, nunca confia só nesta checagem do frontend).
  const generateCompany = companies.find(c => c.company_id === generateForm.company_id);
  const generateSuspended = !!(generateCompany && !generateCompany.cobranca_ativa);
  const submitGenerate = async () => {
    if (!generateForm.company_id) return toast.error("Selecione a empresa");
    if (generateSuspended && !generateForm.override_motivo.trim()) {
      return toast.error("Esta empresa está com a cobrança suspensa — informe o motivo para faturar mesmo assim");
    }
    try {
      await api.post("/admin/commercial/charges/generate", {
        company_id: generateForm.company_id, competencia: generateForm.competencia || null, force: generateForm.force,
        override_suspended: generateSuspended, override_motivo: generateForm.override_motivo,
      });
      toast.success("Faturamento gerado");
      setGenerateOpen(false);
      loadCharges(); loadHistory(); loadOverview();
      if (detailId === generateForm.company_id) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitPay = async () => {
    if (!payForm.data_pagamento || !payForm.forma_pagamento.trim()) return toast.error("Informe a data e a forma de pagamento");
    try {
      await api.post(`/admin/commercial/charges/${payTarget.id}/pay`, payForm);
      toast.success("Cobrança baixada");
      setPayTarget(null); setPayForm({ data_pagamento: "", forma_pagamento: "", observacao: "" });
      loadCharges(); loadHistory(); loadOverview(); if (detailId) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitUndo = async () => {
    if (!undoReason.trim()) return toast.error("Informe o motivo da anulação");
    try {
      await api.post(`/admin/commercial/charges/${undoTarget.id}/undo-payment`, { reason: undoReason });
      toast.success("Baixa anulada");
      setUndoTarget(null); setUndoReason("");
      loadCharges(); loadHistory(); loadOverview(); if (detailId) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitDueDate = async () => {
    if (!dueDateForm.vencimento || !dueDateForm.motivo.trim()) return toast.error("Informe a nova data e o motivo");
    try {
      await api.put(`/admin/commercial/charges/${dueDateTarget.id}/vencimento`, dueDateForm);
      toast.success("Vencimento alterado");
      setDueDateTarget(null); setDueDateForm({ vencimento: "", motivo: "" });
      loadCharges(); loadHistory(); if (detailId) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitCancel = async () => {
    if (!cancelReason.trim()) return toast.error("Informe o motivo do cancelamento");
    try {
      await api.post(`/admin/commercial/charges/${cancelTarget.id}/cancel`, { reason: cancelReason });
      toast.success("Cobrança cancelada");
      setCancelTarget(null); setCancelReason("");
      loadCharges(); loadHistory(); loadOverview(); if (detailId) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Boleto (item 16/17) — passo EXPLÍCITO e SEPARADO de "Faturar": o
  // faturamento já existe antes disso, aqui só se anexa a representação
  // (simulada) de boleto a ele.
  // Gerar boleto/PIX (Fase 2.3, itens "G"/"I") — mesma rota-base, tipo
  // distinto; em conflito de instrumento ativo (409), pede confirmação
  // explícita ANTES de substituir (nunca substitui silenciosamente).
  // Pacote consolidado pós-homologação, Prioridade 4 — BUG CONFIRMADO:
  // "Gerar boleto/PIX" numa cobrança JÁ VENCIDA (sem nenhum instrumento
  // ainda) só abria o modal simples de trocar vencimento, sem juros/
  // multa. Detecta o atraso ANTES de chamar a rota de geração (nunca
  // depende só do 400 do backend, que existe de qualquer forma como
  // segunda linha de defesa) e abre o fluxo de regularização.
  const isChargeOverdue = (charge) => {
    if (!charge?.vencimento) return false;
    const venc = new Date(`${charge.vencimento}T00:00:00`);
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    return venc < hoje;
  };
  // Ajuste Asaas Master — "principal" é ÚNICO globalmente (não por
  // provider, ver admin_set_principal_platform_bank), então a rota de
  // geração precisa seguir QUAL provider está principal agora, em vez de
  // sempre assumir Mercado Pago.
  const principalBankProvider = banks.find(b => b.principal)?.provider;
  const generateInstrument = async (charge, tipo, substituir = false) => {
    if (!charge.boleto_gerado && isChargeOverdue(charge)) { openRegularizar(charge, tipo); return; }
    const sufixo = principalBankProvider === "asaas" ? "-asaas" : "";
    try {
      const { data } = await api.post(`/admin/commercial/charges/${charge.id}/${tipo}${sufixo}`, { substituir_instrumento_anterior: substituir });
      const nomeProvider = data.boleto_modo === "asaas" ? "Asaas" : data.boleto_modo === "mercado_pago" ? "Mercado Pago" : null;
      toast.success(nomeProvider ? `${tipo === "pix" ? "PIX" : "Boleto"} gerado via ${nomeProvider}` : "Boleto gerado (simulado)");
      loadCharges(); loadHistory(); if (detailId) refreshDetail();
    } catch (e) {
      if (e?.response?.status === 409 && !substituir) { setInstrumentConfirm({ charge, tipo }); return; }
      // Segunda linha de defesa: se ainda assim o backend recusar por a
      // cobrança estar vencida (ex.: condição de corrida entre abrir a
      // tela e o dia virar), cai para o mesmo fluxo de regularização em
      // vez de só mostrar o erro cru.
      if (e?.response?.status === 400 && !charge.boleto_gerado) { openRegularizar(charge, tipo); return; }
      toast.error(formatApiError(e));
    }
  };
  const generateBoleto = (charge) => generateInstrument(charge, "boleto");
  const generatePix = (charge) => generateInstrument(charge, "pix");

  // Regularização (Pacote consolidado pós-homologação, Prioridade 4) —
  // cobrança vencida sem instrumento: novo vencimento + juros/multa
  // (marcado por padrão conforme a configuração global) antes de gerar
  // o boleto/PIX real.
  const openRegularizar = async (charge, tipo) => {
    setRegularizarTarget({ charge, tipo });
    setRegularizarForm({ novo_vencimento: "", aplicar_encargos: billingConfig?.aplicar_encargos_reemissao_default ?? true, motivo: "" });
    await refreshRegularizarPreview(charge.id);
  };
  // BUG FINANCEIRO NA REGULARIZAÇÃO — prévia SEMPRE do backend
  // (GET .../preview-regularizacao), nunca recalculada aqui; é a MESMA
  // função central que a confirmação usa (_saas_calcular_regularizacao),
  // então preview exibido e valor cobrado são sempre o mesmo número.
  const refreshRegularizarPreview = async (chargeId) => {
    try {
      const { data } = await api.get(`/admin/commercial/charges/${chargeId}/preview-regularizacao`);
      setRegularizarPreviewData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitRegularizar = async () => {
    if (!regularizarTarget) return;
    const { charge, tipo } = regularizarTarget;
    if (!regularizarForm.novo_vencimento) return toast.error("Informe o novo vencimento");
    if (!regularizarForm.motivo.trim()) return toast.error("Informe o motivo da regularização");
    try {
      await api.post(`/admin/commercial/charges/${charge.id}/regularizar`, {
        novo_vencimento: regularizarForm.novo_vencimento, aplicar_encargos: regularizarForm.aplicar_encargos,
        motivo: regularizarForm.motivo.trim(), instrumento_tipo: tipo,
        data_referencia: regularizarPreviewData?.data_referencia || null,
      });
      toast.success(tipo === "pix" ? "PIX gerado com sucesso" : "Boleto gerado com sucesso");
      setRegularizarTarget(null); setRegularizarPreviewData(null);
      loadCharges(); loadHistory(); if (detailId) refreshDetail();
    } catch (e) {
      // Item 9 — o dia civil (São Paulo) mudou entre abrir a prévia e
      // confirmar (409): nunca cobra silenciosamente um valor diferente do
      // exibido — busca a prévia atualizada e pede para o admin confirmar de novo.
      if (e?.response?.status === 409) { toast.error(formatApiError(e)); await refreshRegularizarPreview(charge.id); return; }
      toast.error(formatApiError(e));
    }
  };
  const regularizarPreview = (() => {
    const p = regularizarPreviewData;
    if (!p) return null;
    if (!regularizarForm.aplicar_encargos || p.dias_atraso <= 0) return { diasAtraso: p.dias_atraso, multa: 0, juros: 0, novoValor: p.valor_base };
    return { diasAtraso: p.dias_atraso, multa: p.multa_valor, juros: p.juros_valor, novoValor: p.valor_final };
  })();
  const confirmSubstituteInstrument = async () => {
    if (!instrumentConfirm) return;
    const { charge, tipo } = instrumentConfirm;
    setInstrumentConfirm(null);
    await generateInstrument(charge, tipo, true);
  };

  // "Sincronizar situação" (Fase 2.3, item "K") — consulta a situação REAL
  // no Mercado Pago; nunca decide nada sozinho no frontend.
  const syncCharge = async (charge) => {
    try {
      const { data } = await api.post(`/admin/commercial/charges/${charge.id}/sincronizar`);
      toast.success(`Situação sincronizada: ${BOLETO_STATUS_LABEL[data.boleto_status] || data.boleto_status || "atualizada"}`);
      loadCharges(); loadHistory(); if (detailId) refreshDetail();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // "Alterar vencimento / Reemitir boleto" (Fase 2.3, item "C") — única
  // forma de mudar o vencimento depois de um instrumento real emitido.
  const openReemitir = async (charge) => {
    setReemitirTarget(charge);
    setReemitirForm({ novo_vencimento: "", aplicar_encargos: billingConfig?.aplicar_encargos_reemissao_default ?? true, motivo: "" });
    await refreshReemitirPreview(charge.id);
  };
  // BUG FINANCEIRO NA REGULARIZAÇÃO — mesma prévia do backend usada em
  // "Regularizar" (mesmo endpoint: a fórmula não muda com ou sem
  // instrumento já emitido, só o vencimento/valor_final ATUAIS da cobrança).
  const refreshReemitirPreview = async (chargeId) => {
    try {
      const { data } = await api.get(`/admin/commercial/charges/${chargeId}/preview-regularizacao`);
      setReemitirPreviewData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitReemitir = async () => {
    if (!reemitirTarget) return;
    if (!reemitirForm.novo_vencimento) return toast.error("Informe o novo vencimento");
    if (!reemitirForm.motivo.trim()) return toast.error("Informe o motivo da reemissão");
    try {
      await api.post(`/admin/commercial/charges/${reemitirTarget.id}/reemitir`, {
        novo_vencimento: reemitirForm.novo_vencimento, aplicar_encargos: reemitirForm.aplicar_encargos, motivo: reemitirForm.motivo.trim(),
        data_referencia: reemitirPreviewData?.data_referencia || null,
      });
      // Pacote consolidado pós-homologação, Prioridade 6 — mensagem
      // específica por instrumento, nunca a genérica "Boleto/PIX reemitido".
      toast.success(reemitirTarget.instrumento_tipo === "pix" ? "PIX reemitido com sucesso" : "Boleto reemitido com sucesso");
      setReemitirTarget(null); setReemitirPreviewData(null);
      loadCharges(); loadHistory(); if (detailId) refreshDetail();
    } catch (e) {
      if (e?.response?.status === 409) { toast.error(formatApiError(e)); await refreshReemitirPreview(reemitirTarget.id); return; }
      toast.error(formatApiError(e));
    }
  };
  // Prévia SEMPRE do backend (GET .../preview-regularizacao) — nunca
  // recalculada aqui; era exatamente essa segunda implementação (em
  // JS/float, com a data local do navegador) a causa raiz da divergência
  // entre o valor exibido e o valor realmente cobrado no provider.
  const reemitirPreview = (() => {
    const p = reemitirPreviewData;
    if (!p) return null;
    if (!reemitirForm.aplicar_encargos || p.dias_atraso <= 0) return { diasAtraso: p.dias_atraso, multa: 0, juros: 0, novoValor: p.valor_base };
    return { diasAtraso: p.dias_atraso, multa: p.multa_valor, juros: p.juros_valor, novoValor: p.valor_final };
  })();

  // Configuração global de juros/multa (Fase 2.3, item "A"/"P").
  const saveBillingConfig = async () => {
    try {
      const { data } = await api.put("/admin/commercial/billing-config", {
        multa_percentual: Number(billingConfigForm.multa_percentual) || 0,
        juros_mensal_percentual: Number(billingConfigForm.juros_mensal_percentual) || 0,
        aplicar_encargos_reemissao_default: billingConfigForm.aplicar_encargos_reemissao_default,
        whatsapp_financeiro_numero: billingConfigForm.whatsapp_financeiro_numero || "",
      });
      setBillingConfig(data);
      toast.success("Configuração de cobrança salva");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Configuração Central de E-mail de Cobrança (itens 1-14 do pedido) —
  // identidade/SMTP da própria Codexpy, administrável sem SSH/deploy.
  const saveEmailSettings = async () => {
    try {
      const { data } = await api.put("/admin/commercial/email-settings", {
        active: emailSettingsForm.active,
        from_name: emailSettingsForm.from_name, from_email: emailSettingsForm.from_email,
        reply_to: emailSettingsForm.reply_to || null,
        smtp_host: emailSettingsForm.smtp_host, smtp_port: Number(emailSettingsForm.smtp_port) || 587,
        security: emailSettingsForm.security, smtp_username: emailSettingsForm.smtp_username,
        // Nunca envia "" como se fosse uma senha nova — ausência/vazio
        // preserva a senha já salva (item 23).
        smtp_password: emailSettingsForm.smtp_password ? emailSettingsForm.smtp_password : null,
      });
      setEmailSettings(data);
      setEmailSettingsForm(f => ({ ...f, smtp_password: "" }));
      setEmailSettingsEditing(false);
      toast.success("E-mail de cobrança salvo");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const testEmailConnection = async () => {
    setTestingEmailConnection(true);
    try {
      const { data } = await api.post("/admin/commercial/email-settings/test-connection");
      if (data.ok) toast.success(data.message); else toast.error(data.message);
      loadEmailSettings();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTestingEmailConnection(false); }
  };
  const sendTestEmail = async () => {
    if (!testEmailTo.trim()) { toast.error("Informe o e-mail de destino do teste"); return; }
    setSendingTestEmail(true);
    try {
      const { data } = await api.post("/admin/commercial/email-settings/send-test", { to: testEmailTo.trim() });
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingTestEmail(false); }
  };

  // Compartilhar (itens 18-19/25) — E-mail reaproveita a infraestrutura já
  // existente; WhatsApp é o mecanismo GRATUITO (wa.me), nunca a integração
  // automática Gold. Nunca afirma "enviado" no WhatsApp — só abre a
  // conversa já pronta; quem confirma é o usuário.
  const shareCharge = async (charge, channel) => {
    try {
      if (channel === "whatsapp") {
        const { data } = await api.post(`/admin/commercial/charges/${charge.id}/whatsapp-share-link`);
        window.open(data.wa_link, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp aberto com a mensagem pronta — confirme o envio por lá");
      } else {
        const { data } = await api.post(`/admin/commercial/charges/${charge.id}/email-share`);
        if (data.sent) toast.success("Faturamento enviado por e-mail");
        else toast.info(data.reason || "Não foi possível enviar por e-mail");
      }
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Pacote consolidado pós-homologação, Prioridade 17 — reenvio manual do
  // e-mail automático de "mensalidade disponível" (diferente de
  // shareCharge/"Compartilhar", que é o link de visualização pública do
  // faturamento — aqui é o e-mail idempotente enviado automaticamente na
  // criação da cobrança, que o Admin Master pode reenviar quando quiser).
  const resendAvailabilityEmail = async (charge) => {
    try {
      const { data } = await api.post(`/admin/commercial/charges/${charge.id}/email-mensalidade/reenviar`);
      if (data.sent) toast.success("E-mail de mensalidade reenviado");
      else toast.info("Não foi possível reenviar — verifique se a empresa tem e-mail cadastrado");
      loadHistory();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Copiar boleto real (linha digitável/link) para a área de transferência
  // — nunca abre uma URL arbitrária controlada pelo usuário, sempre a que
  // o próprio Mercado Pago devolveu (boleto_url).
  const copyToClipboard = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copiada`); }
    catch { toast.error("Não foi possível copiar — copie manualmente"); }
  };

  // Bancos da Plataforma (Fase 2) — CRUD do(s) provedor(es) bancário/de
  // pagamento da ALTAFORJA/LOOTUS (nunca de um tenant — isso continua em
  // Configurações → Bancos, /config/banks, intocado). access_token só é
  // enviado quando preenchido no formulário (nunca sobrescreve uma
  // credencial já salva com um valor vazio).
  const openNewBank = () => { setBankEditing(null); setBankForm({ nome: "", provider: "mercado_pago", environment: "sandbox", access_token: "", webhook_secret: "", active: true }); setBankOpen(true); };
  const openEditBank = (b) => { setBankEditing(b); setBankForm({ nome: b.nome, provider: b.provider, environment: b.environment, access_token: "", webhook_secret: "", active: b.active }); setBankOpen(true); };
  const saveBank = async () => {
    if (!bankForm.nome.trim()) return toast.error("Informe um nome para o provedor");
    try {
      if (bankEditing) await api.put(`/admin/commercial/banks/${bankEditing.id}`, bankForm);
      else await api.post("/admin/commercial/banks", bankForm);
      toast.success("Provedor bancário salvo");
      setBankOpen(false); loadBanks();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleBankActive = async (b) => {
    try {
      await api.post(`/admin/commercial/banks/${b.id}/${b.active ? "deactivate" : "activate"}`);
      loadBanks();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setPrincipalBank = async (b) => {
    try { await api.post(`/admin/commercial/banks/${b.id}/set-principal`); toast.success(`${b.nome} definido como principal`); loadBanks(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsetPrincipalBank = async (b) => {
    if (!window.confirm(`Remover "${b.nome}" como provedor principal? Enquanto nenhum outro provedor for definido como principal, a geração de boleto real ficará indisponível.`)) return;
    try { await api.post(`/admin/commercial/banks/${b.id}/unset-principal`); toast.success(`${b.nome} não é mais o principal`); loadBanks(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const testBankConnection = async (b) => {
    setBankTesting(b.id);
    try {
      const { data } = await api.post(`/admin/commercial/banks/${b.id}/test-connection`);
      if (data.ok) toast.success(data.message); else toast.error(data.message);
      loadBanks();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBankTesting(null); }
  };

  const BANK_CONNECTION_LABEL = { connected: "Conectado", failed: "Falha na conexão", not_tested: "Ainda não testado" };
  const BANK_CONNECTION_TONE = { connected: "bg-emerald-100 text-emerald-800", failed: "bg-red-100 text-red-800", not_tested: "bg-slate-100 text-slate-600" };

  // PIX da Altaforja — mesmo CRUD do tenant (Configurações → Bancos e
  // Pagamentos → PIX por chave), só que apontado ao endpoint admin-scoped.
  // A chave real nunca volta fora de /reveal — editar busca de novo, pois
  // o PUT substitui o registro inteiro.
  const openNewPixKeyAccount = () => {
    setPkaEditing(null);
    setPkaForm({ name: "", institution_name: "", holder_name: "", key_type: "cpf", pix_key: "", active: true });
    setPkaShowKey(false);
    setPkaOpen(true);
  };
  const openEditPixKeyAccount = async (acc) => {
    setPkaEditing(acc);
    setPkaForm({ name: acc.name, institution_name: acc.institution_name || "", holder_name: acc.holder_name, key_type: acc.key_type, pix_key: "", active: acc.active !== false });
    setPkaShowKey(false);
    setPkaOpen(true);
    try {
      const { data } = await api.post(`/admin/commercial/pix-key-accounts/${acc.id}/reveal`);
      setPkaForm(f => ({ ...f, pix_key: data.pix_key, key_type: data.key_type }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const savePixKeyAccount = async () => {
    if (!pkaForm.name.trim()) return toast.error("Informe um nome para identificar esta chave");
    if (!pkaForm.holder_name.trim()) return toast.error("Informe o titular da conta");
    if (!pkaForm.pix_key.trim()) return toast.error("Informe a chave Pix");
    const body = {
      name: pkaForm.name, institution_name: pkaForm.institution_name.trim() || undefined,
      holder_name: pkaForm.holder_name, key_type: pkaForm.key_type,
      pix_key: pkaForm.pix_key, active: pkaForm.active,
    };
    try {
      if (pkaEditing) await api.put(`/admin/commercial/pix-key-accounts/${pkaEditing.id}`, body);
      else await api.post("/admin/commercial/pix-key-accounts", body);
      toast.success(`Chave Pix da ${PLATFORM_COMPANY_SHORT_NAME} salva`);
      setPkaOpen(false); loadPixKeyAccounts();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePixKeyAccountActive = async (acc) => {
    try {
      await api.post(`/admin/commercial/pix-key-accounts/${acc.id}/${acc.active ? "deactivate" : "activate"}`);
      toast.success(acc.active ? "Chave desativada" : "Chave ativada");
      loadPixKeyAccounts();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setDefaultPixKeyAccount = async (acc) => {
    try { const { data } = await api.post(`/admin/commercial/pix-key-accounts/${acc.id}/set-default`); setPixKeyAccounts(data); toast.success(`"${acc.name}" definida como padrão`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const unsetDefaultPixKeyAccount = async (acc) => {
    try { const { data } = await api.post(`/admin/commercial/pix-key-accounts/${acc.id}/unset-default`); setPixKeyAccounts(data); toast.success(`"${acc.name}" deixou de ser padrão`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const revealPixKey = async (acc) => {
    setPkaRevealing(acc.id);
    try { const { data } = await api.post(`/admin/commercial/pix-key-accounts/${acc.id}/reveal`); setPkaRevealOpen(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setPkaRevealing(null); }
  };
  const PIX_KEY_TYPE_LABELS = { cpf: "CPF", cnpj: "CNPJ", email: "E-mail", telefone: "Telefone", aleatoria: "Chave aleatória" };

  // Faturamento SaaS (itens 11/12/16-18) — fluxo conceitual: Faturar ->
  // Faturamento SaaS (esta tabela) -> Gerar boleto -> Compartilhar ->
  // Pagamento/baixa. "Gerar boleto" gera um boleto REAL via Mercado Pago
  // quando há um provedor bancário PRINCIPAL ativo (aba Bancos); sem ele,
  // continua o modo simulado pré-existente (comportamento intocado).
  const ChargesTable = ({ list, showActions, batchEnabled }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            {batchEnabled && (
              <th className="py-3 pl-4 pr-1 w-8">
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title="Selecionar todos (respeitando o filtro atual)" data-testid="cobrancas-selecionar-todos" />
              </th>
            )}
            <th className="py-3 px-4">Nº</th><th>Empresa</th><th>Competência</th><th>Vencimento</th><th>Plano</th><th>Valor</th><th>Situação</th>
            <th>Provider</th><th>Instrumento</th><th>E-mail</th>
            {showActions && <th className="text-right pr-4">Ações</th>}
          </tr>
        </thead>
        <tbody>
          {list.map(c => (
            <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
              {batchEnabled && (
                <td className="pl-4 pr-1">
                  {c.status !== "cancelado" && (
                    <input type="checkbox" checked={selectedChargeIds.has(c.id)} onChange={() => toggleSelectCharge(c.id)} data-testid={`cobranca-selecionar-${c.id}`} />
                  )}
                </td>
              )}
              <td className="py-3 px-4 text-slate-500">{c.numero}</td>
              <td>
                <button type="button" className="font-medium text-slate-900 hover:underline" onClick={() => openDetail(c.company_id)}>{c.empresa_nome}</button>
              </td>
              <td className="text-slate-500">{competenciaFromInternal(c.competencia)}</td>
              <td className="text-slate-500">
                {/* Pacote consolidado pós-homologação, Prioridade 5 — depois de emitido, a
                    fonte de verdade é a data EFETIVA confirmada pelo provedor
                    (provider_due_date), quando existir; nunca mostra a data solicitada como
                    se fosse a confirmada sem indicar a diferença. */}
                {fmtDate(c.provider_due_date || c.requested_due_date || c.vencimento)}
                {c.provider_due_date && c.requested_due_date && c.provider_due_date !== c.requested_due_date && (
                  <div className="text-xs text-amber-600" title={`Solicitado: ${fmtDate(c.requested_due_date)}`}>
                    ajustado pelo banco (solicitado {fmtDate(c.requested_due_date)})
                  </div>
                )}
              </td>
              <td className="text-slate-500">{c.plano_nome_snapshot}</td>
              <td className="font-medium text-slate-900">{money(c.valor_final)}</td>
              <td>
                <div className="flex items-center gap-1.5">
                  {chargeStatusBadge(c)}
                  {c.boleto_gerado && c.boleto_modo === "simulado" && (
                    <Badge className="bg-slate-100 text-slate-600" title="Boleto simulado (sem integração bancária real)">Boleto simulado</Badge>
                  )}
                  {/* Finalização do Faturamento Master, Parte F — antes só
                      reconhecia boleto_modo === "mercado_pago"; uma cobrança
                      Asaas ficava sem badge/ação nenhuma nesta tabela. */}
                  {c.boleto_gerado && isRealInstrumentMode(c.boleto_modo) && (
                    <Badge className={BOLETO_STATUS_TONE[c.boleto_status] || "bg-slate-100 text-slate-600"} title={`${c.instrumento_tipo === "pix" ? "PIX" : "Boleto"} real via ${platformProviderLabel(c.boleto_modo)}`}>
                      {c.instrumento_tipo === "pix" ? "PIX" : "Boleto"} · {BOLETO_STATUS_LABEL[c.boleto_status] || platformProviderLabel(c.boleto_modo)}
                    </Badge>
                  )}
                  {/* Item 28 — nunca mais a frase longa fixa na linha: só o
                      ⚠ DETALHE clicável, com o texto completo num popover. */}
                  {c.status === "pago" && c.baixa_origem === "manual" && isRealInstrumentMode(c.boleto_modo) && ["waiting_payment", "processing"].includes(c.boleto_status) && (
                    <StatusDetalhe testId={`cobranca-detalhe-${c.id}`}>
                      Pago por baixa administrativa. O {c.instrumento_tipo === "pix" ? "PIX" : "boleto"} externo ainda consta como aguardando pagamento no {platformProviderLabel(c.boleto_modo)}.
                    </StatusDetalhe>
                  )}
                </div>
              </td>
              {/* Item 9 — colunas mínimas da tela de Faturamento SaaS
                  preparada para centenas/milhares de empresas: Provider/
                  Instrumento/E-mail sempre visíveis, nunca só num popover. */}
              <td className="text-slate-500">{platformProviderLabel(c.boleto_modo) || "—"}</td>
              <td className="text-slate-500">
                {c.boleto_modo === "simulado" ? "Boleto (simulado)"
                  : c.boleto_gerado ? (c.instrumento_tipo === "pix" ? "PIX" : "Boleto")
                  : c.emissao_disponivel ? "Aguardando emissão"
                  : <span title="Fora da janela de emissão (D-10)">Disponível em {fmtDate(c.emissao_disponivel_em)}</span>}
              </td>
              <td className="text-slate-500">
                {c.batch_email_enviado_em
                  ? <span title={new Date(c.batch_email_enviado_em).toLocaleString("pt-BR")}>Enviado</span>
                  : "Não enviado"}
              </td>
              {showActions && (() => {
                const instrumentoAtivo = c.boleto_gerado && isRealInstrumentMode(c.boleto_modo) && ["waiting_payment", "processing"].includes(c.boleto_status);
                const isPix = c.instrumento_tipo === "pix";
                return (
                <td className="text-right pr-4 space-x-1 whitespace-nowrap">
                  {/* Item 3/18/19 — antes de D-10, os botões ficam
                      DESABILITADOS com o motivo visível (nunca escondidos —
                      item 5). c.emissao_disponivel nunca é false para um
                      título já vencido (D-10 é trivialmente satisfeito),
                      então isto nunca bloqueia o caso de "vencida sem
                      instrumento" do item 20/CASO A. */}
                  {c.status !== "cancelado" && !c.boleto_gerado && c.emissao_disponivel === false && (
                    <span className="text-xs text-slate-500" title="Fora da janela de emissão (D-10)">
                      Disponível em {fmtDate(c.emissao_disponivel_em)}
                    </span>
                  )}
                  {c.status !== "cancelado" && !c.boleto_gerado && c.emissao_disponivel !== false && (
                    <>
                      {/* Pacote consolidado pós-homologação, Prioridade 7 — ícone de
                          código de barras (Barcode), visualmente reconhecível como
                          boleto/documento bancário, nunca um ícone genérico. */}
                      <Button size="sm" variant="outline" onClick={() => generateBoleto(c)} title="Gerar boleto" data-testid={`cobranca-gerar-boleto-${c.id}`}><Barcode size={14} /> Gerar boleto</Button>
                      <Button size="sm" variant="outline" onClick={() => generatePix(c)} title="Gerar PIX" data-testid={`cobranca-gerar-pix-${c.id}`}><QrCode size={14} /> Gerar PIX</Button>
                    </>
                  )}
                  {isRealInstrumentMode(c.boleto_modo) && !isPix && c.boleto_url && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => window.open(c.boleto_url, "_blank", "noopener,noreferrer")} title="Ver boleto" data-testid={`cobranca-ver-boleto-${c.id}`}><Barcode size={16} /></Button>
                      {c.boleto_linha_digitavel && (
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(c.boleto_linha_digitavel, "Linha digitável")} title="Copiar linha digitável" data-testid={`cobranca-copiar-linha-${c.id}`}><Copy size={16} /></Button>
                      )}
                    </>
                  )}
                  {isRealInstrumentMode(c.boleto_modo) && isPix && instrumentoAtivo && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setPixModalCharge(c)} title="Ver QR Code" data-testid={`cobranca-ver-pix-${c.id}`}><QrCode size={16} /></Button>
                      {c.pix_qr_code && (
                        <Button size="sm" variant="ghost" onClick={() => copyToClipboard(c.pix_qr_code, "PIX Copia e Cola")} title="Copiar PIX Copia e Cola" data-testid={`cobranca-copiar-pix-${c.id}`}><Copy size={16} /></Button>
                      )}
                      {c.boleto_url && (
                        <Button size="sm" variant="ghost" onClick={() => window.open(c.boleto_url, "_blank", "noopener,noreferrer")} title="Abrir detalhes" data-testid={`cobranca-detalhes-pix-${c.id}`}><ExternalLink size={16} /></Button>
                      )}
                    </>
                  )}
                  {c.status !== "cancelado" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" title="Compartilhar" data-testid={`cobranca-compartilhar-${c.id}`}><Share2 size={16} /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => shareCharge(c, "email")} data-testid={`cobranca-compartilhar-email-${c.id}`}><Mail size={14} /> E-mail</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => shareCharge(c, "whatsapp")} data-testid={`cobranca-compartilhar-whatsapp-${c.id}`}><MessageCircle size={14} /> WhatsApp</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => resendAvailabilityEmail(c)} data-testid={`cobranca-reenviar-email-mensalidade-${c.id}`}><Mail size={14} /> Reenviar e-mail de mensalidade</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {instrumentoAtivo && (
                    <Button size="sm" variant="ghost" onClick={() => syncCharge(c)} title="Sincronizar situação" data-testid={`cobranca-sincronizar-${c.id}`}><RefreshCw size={16} /></Button>
                  )}
                  {c.status === "em_aberto" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { setPayTarget(c); setPayForm({ data_pagamento: "", forma_pagamento: "", observacao: "" }); }} data-testid={`cobranca-baixar-${c.id}`}>Baixar</Button>
                      {instrumentoAtivo ? (
                        <Button size="sm" variant="ghost" onClick={() => openReemitir(c)} title="Alterar vencimento / Reemitir" data-testid={`cobranca-reemitir-${c.id}`}><CalendarClock size={16} /></Button>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setDueDateTarget(c); setDueDateForm({ vencimento: c.vencimento, motivo: "" }); }} title="Alterar vencimento" data-testid={`cobranca-vencimento-${c.id}`}><CalendarClock size={16} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => { setCancelTarget(c); setCancelReason(""); }} title="Cancelar" data-testid={`cobranca-cancelar-${c.id}`}><Ban size={16} /></Button>
                        </>
                      )}
                    </>
                  )}
                  {c.status === "pago" && (
                    <Button size="sm" variant="ghost" onClick={() => { setUndoTarget(c); setUndoReason(""); }} title="Anular baixa" data-testid={`cobranca-anular-${c.id}`}><RotateCcw size={16} /></Button>
                  )}
                </td>
                );
              })()}
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={showActions ? 8 : 7} className="py-8 text-center text-slate-400">Nenhum faturamento encontrado</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Briefcase size={28} /> Gestão Comercial</h1>
        <p className="text-slate-500 text-sm">Cobrança da LOOTUS às empresas clientes da plataforma — não confundir com o faturamento operacional de cada empresa</p>
      </div>

      <Tabs defaultValue="visao-geral">
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="visao-geral" data-testid="tab-visao-geral">Visão geral</TabsTrigger>
          <TabsTrigger value="empresas" data-testid="tab-comercial-empresas">Empresas</TabsTrigger>
          <TabsTrigger value="cobrancas" data-testid="tab-cobrancas">Faturamento SaaS</TabsTrigger>
          <TabsTrigger value="inadimplencia" data-testid="tab-inadimplencia"><ShieldAlert size={14} className="mr-1" /> Inadimplência</TabsTrigger>
          <TabsTrigger value="bancos" data-testid="tab-bancos"><Landmark size={14} className="mr-1" /> Bancos</TabsTrigger>
          <TabsTrigger value="config-cobranca" data-testid="tab-config-cobranca"><Settings size={14} className="mr-1" /> Configurações de cobrança</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><History size={14} className="mr-1" /> Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="visao-geral" className="mt-4 space-y-4">
          {overview && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Briefcase} label="Empresas ativas" value={overview.empresas_ativas} tone="blue" />
                <StatCard icon={AlertTriangle} label="Cobranças vencidas" value={overview.cobrancas_vencidas} tone="red" />
                <StatCard icon={CheckCircle2} label="Em aberto" value={overview.cobrancas_em_aberto} tone="amber" />
                <StatCard icon={PauseCircle} label="Cobrança suspensa" value={overview.cobrancas_suspensas} tone="slate" />
                <StatCard icon={TrendingUp} label={`Receita prevista (${overview.competencia})`} value={money(overview.receita_prevista_mes)} tone="emerald" />
                <StatCard icon={Wallet} label={`Receita recebida (${overview.competencia})`} value={money(overview.receita_recebida_mes)} tone="emerald" />
                <StatCard icon={CheckCircle2} label="Pagas no mês" value={overview.cobrancas_pagas_mes} tone="slate" />
                <StatCard icon={Percent} label="Descontos ativos" value={overview.descontos_ativos} tone="slate" />
              </div>
              <Card className="p-4">
                <div className="font-heading font-semibold mb-2">Empresas por plano</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(overview.por_plano).map(([nome, qtd]) => (
                    <Badge key={nome} className="bg-slate-100 text-slate-700">{nome}: {qtd}</Badge>
                  ))}
                </div>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="empresas" className="mt-4">
          <Card className="p-0 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex flex-wrap gap-2 items-end">
              <div className="w-56">
                <Label>Buscar</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-7" value={companyFilters.q} onChange={e => setCompanyFilters({ ...companyFilters, q: e.target.value })} data-testid="comercial-empresas-busca" />
                </div>
              </div>
              <div className="w-48">
                <Label>Plano</Label>
                <Select value={companyFilters.plano_id || "todos"} onValueChange={v => setCompanyFilters({ ...companyFilters, plano_id: v === "todos" ? "" : v })}>
                  <SelectTrigger data-testid="comercial-empresas-plano"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os planos</SelectItem>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label>Cobrança</Label>
                <Select value={companyFilters.cobranca_ativa === "" ? "todas" : String(companyFilters.cobranca_ativa)}
                  onValueChange={v => setCompanyFilters({ ...companyFilters, cobranca_ativa: v === "todas" ? "" : v === "true" })}>
                  <SelectTrigger data-testid="comercial-empresas-cobranca"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="true">Ativa</SelectItem>
                    <SelectItem value="false">Suspensa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                <input type="checkbox" checked={companyFilters.vencidas_apenas} onChange={e => setCompanyFilters({ ...companyFilters, vencidas_apenas: e.target.checked })} /> Só vencidas
              </label>
              <Button variant="ghost" size="sm" className="mb-2" onClick={() => setCompanyFilters({ q: "", plano_id: "", cobranca_ativa: "", vencidas_apenas: false })} data-testid="comercial-empresas-limpar-filtros">
                <X size={14} /> Limpar filtros
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="py-3 px-4">Empresa</th><th>Plano</th><th>Mensalidade</th><th>Desconto</th><th>Valor efetivo</th>
                    <th>Próximo vencimento</th><th>Situação</th><th>Contrato</th><th className="text-right pr-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(c => (
                    <tr key={c.company_id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <button type="button" className="font-medium text-slate-900 hover:underline" onClick={() => openDetail(c.company_id)} data-testid={`comercial-empresa-${c.company_id}`}>
                          {c.nome_fantasia || c.razao_social}
                        </button>
                      </td>
                      <td className="text-slate-500">{c.plano_nome || "Sem plano"}</td>
                      <td className="text-slate-500">{money(c.valor_base)}</td>
                      <td className="text-slate-500">{c.desconto_valor > 0 ? money(c.desconto_valor) : "-"}</td>
                      <td className="font-medium text-slate-900">{money(c.valor_efetivo)}</td>
                      <td className={c.vencida ? "text-red-600 font-medium" : "text-slate-500"}>{c.proximo_vencimento ? fmtDate(c.proximo_vencimento) : "-"}{c.vencida ? " (vencida)" : ""}</td>
                      <td><Badge className={c.cobranca_ativa ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{c.cobranca_ativa ? "Ativa" : "Suspensa"}</Badge></td>
                      <td>{c.contrato_assinado ? <Badge className="bg-emerald-100 text-emerald-800">Assinado</Badge> : <Badge className="bg-slate-200 text-slate-700">Pendente</Badge>}</td>
                      <td className="text-right pr-4">
                        <Button size="sm" variant="outline" onClick={() => openDetail(c.company_id)} data-testid={`comercial-empresa-detalhe-${c.company_id}`}>Ver condição</Button>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">Nenhuma empresa encontrada</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="cobrancas" className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2 items-end justify-between">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="w-64">
                <Label>Empresa</Label>
                <EmpresaFilterCombobox
                  value={chargeFilters.company_id}
                  companies={companies}
                  onChange={company_id => setChargeFilters({ ...chargeFilters, company_id })}
                />
              </div>
              <div className="w-32">
                <Label>Competência</Label>
                <Input placeholder="MM-AAAA" maxLength={7} value={chargeFilters.competencia} onChange={e => setChargeFilters({ ...chargeFilters, competencia: maskCompetencia(e.target.value) })} data-testid="cobrancas-competencia" />
              </div>
              <div className="w-44">
                <Label>Status</Label>
                <Select value={chargeFilters.status || "todos"} onValueChange={v => setChargeFilters({ ...chargeFilters, status: v === "todos" ? "" : v })}>
                  <SelectTrigger data-testid="cobrancas-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {Object.entries(CHARGE_STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                <input type="checkbox" checked={chargeFilters.vencidas_apenas} onChange={e => setChargeFilters({ ...chargeFilters, vencidas_apenas: e.target.checked })} /> Só vencidas
              </label>
              <Button variant="ghost" size="sm" className="mb-2" onClick={() => setChargeFilters({ company_id: "", status: "", competencia: "", vencidas_apenas: false })} data-testid="cobrancas-limpar-filtros">
                <X size={14} /> Limpar filtros
              </Button>
            </div>
            {/* Item 5/11 — o ciclo normal agora é AUTOMÁTICO
                (ensure_current_period_charges); "Faturar" deixou de ser a
                ação principal e vira uma exceção administrativa explícita
                (competência retroativa/antecipada fora do ciclo padrão). */}
            <Button variant="outline" onClick={() => openGenerate("")} data-testid="cobranca-gerar"><Plus size={16} /> Gerar título manual</Button>
          </div>

          {/* Finalização do Faturamento Master, Partes B/C — ações em
              lote: nunca bloqueia a tela (os endpoints só enfileiram, ver
              server.py); o progresso aparece aqui via polling. */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-slate-600">
              {selectedChargeIds.size > 0 ? `${selectedChargeIds.size} cobrança(s) selecionada(s)` : "Nenhuma cobrança selecionada"}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" disabled={selectedChargeIds.size === 0 || batchStarting} onClick={startBatchGenerate} data-testid="cobrancas-lote-gerar-boletos">
                <Barcode size={14} /> Gerar boletos selecionados
              </Button>
              <Button size="sm" variant="outline" disabled={selectedChargeIds.size === 0 || batchStarting} onClick={() => startBatchEmail(false)} data-testid="cobrancas-lote-enviar-email">
                <Mail size={14} /> Enviar por e-mail
              </Button>
              {/* Item 22 — reenvio NUNCA é o comportamento padrão do botão
                  acima (que pula silenciosamente quem já recebeu); só esta
                  ação separada e explícita força o reenvio. */}
              <Button size="sm" variant="ghost" disabled={selectedChargeIds.size === 0 || batchStarting} onClick={() => startBatchEmail(true)} data-testid="cobrancas-lote-reenviar-email" title="Reenviar mesmo para quem já recebeu">
                <RefreshCw size={14} /> Reenviar
              </Button>
            </div>
          </div>
          {batchJob && (
            <Card className="p-4 border-blue-200 bg-blue-50" data-testid="cobrancas-lote-progresso">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-blue-900">
                  Lote {batchJob.tipo === "gerar_boletos" ? "de geração de boletos/PIX" : "de envio de e-mail"} —{" "}
                  {batchJob.status !== "concluido" ? "processando..." : batchJob.resultado === "com_falhas" ? "concluído com falhas" : "concluído"}
                </div>
                <div className="flex gap-2">
                  {batchJob.status === "concluido" && (batchJob.items || []).length > 0 && (
                    <Button size="sm" variant="ghost" onClick={() => setShowBatchDetails(v => !v)} data-testid="cobrancas-lote-ver-detalhes">
                      {showBatchDetails ? "Ocultar detalhes" : "Ver detalhes"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => { setBatchJob(null); setShowBatchDetails(false); }} data-testid="cobrancas-lote-fechar">Fechar</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge className="bg-slate-100 text-slate-600">Total: {batchJob.total}</Badge>
                {Object.entries(batchJob.contadores || {}).map(([status, count]) => (
                  <Badge key={status} className="bg-white text-slate-700 border border-slate-200">{BATCH_ITEM_STATUS_LABEL[status] || status}: {count}</Badge>
                ))}
              </div>
              {/* Item 8 — detalhamento por empresa, nunca só a contagem
                  agregada; motivo sempre amigável (vem pronto do backend,
                  nunca traceback). */}
              {showBatchDetails && (
                <div className="mt-3 max-h-72 overflow-y-auto border border-blue-200 rounded-md bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 text-left sticky top-0">
                      <tr><th className="py-2 px-3">Empresa</th><th>Competência</th><th>Resultado</th><th>Motivo</th></tr>
                    </thead>
                    <tbody>
                      {(batchJob.items || []).map(it => (
                        <tr key={it.id} className="border-t border-slate-100">
                          <td className="py-1.5 px-3">{it.empresa_nome || "-"}</td>
                          <td className="text-slate-500">{it.competencia ? competenciaFromInternal(it.competencia) : "-"}</td>
                          <td className="text-slate-700">{BATCH_ITEM_STATUS_LABEL[it.status] || it.status}</td>
                          <td className="text-slate-500">{it.motivo || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          <Card className="p-0 overflow-hidden"><ChargesTable list={visibleCharges} showActions batchEnabled /></Card>
        </TabsContent>

        {/* Contrato Permanente + Régua de Inadimplência (itens 14/15/16) —
            painel dedicado. Indicadores e tabela vêm do MESMO endpoint
            (GET /admin/commercial/inadimplencia), calculados sobre
            saas_charges vencidas — nunca percentual/tendência inventada. */}
        <TabsContent value="inadimplencia" className="mt-4 space-y-3">
          {inadimplencia.indicadores && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Briefcase} label="Empresas em atraso" value={inadimplencia.indicadores.empresas_em_atraso} tone="red" />
              <StatCard icon={Wallet} label="Valor total vencido" value={money(inadimplencia.indicadores.valor_total_vencido)} tone="red" />
              <StatCard icon={Mail} label="1º aviso" value={inadimplencia.indicadores.aviso_1} tone="amber" />
              <StatCard icon={Mail} label="2º aviso" value={inadimplencia.indicadores.aviso_2} tone="amber" />
              <StatCard icon={AlertTriangle} label="Aviso final" value={inadimplencia.indicadores.aviso_final} tone="amber" />
              <StatCard icon={Ban} label="Elegíveis para suspensão" value={inadimplencia.indicadores.elegivel_suspensao} tone="red" />
              <StatCard icon={ShieldAlert} label="Elegíveis para rescisão" value={inadimplencia.indicadores.elegivel_rescisao} tone="slate" />
              <StatCard icon={ShieldAlert} label="Elegíveis p/ análise de negativação" value={inadimplencia.indicadores.elegivel_negativacao} tone="slate" />
            </div>
          )}

          <Card className="p-4">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="w-56">
                <Label>Empresa ou contrato</Label>
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-7" value={inadFilters.q} onChange={e => setInadFilters({ ...inadFilters, q: e.target.value })} data-testid="inad-busca" />
                </div>
              </div>
              <div className="w-44">
                <Label>Plano</Label>
                <Select value={inadFilters.plano_id || "todos"} onValueChange={v => setInadFilters({ ...inadFilters, plano_id: v === "todos" ? "" : v })}>
                  <SelectTrigger data-testid="inad-plano"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-56">
                <Label>Etapa</Label>
                <Select value={inadFilters.etapa || "todas"} onValueChange={v => setInadFilters({ ...inadFilters, etapa: v === "todas" ? "" : v })}>
                  <SelectTrigger data-testid="inad-etapa"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {Object.entries(DELINQUENCY_STAGE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <Label>Dias (mín.)</Label>
                <Input type="number" min="0" value={inadFilters.dias_min} onChange={e => setInadFilters({ ...inadFilters, dias_min: e.target.value })} data-testid="inad-dias-min" />
              </div>
              <div className="w-24">
                <Label>Dias (máx.)</Label>
                <Input type="number" min="0" value={inadFilters.dias_max} onChange={e => setInadFilters({ ...inadFilters, dias_max: e.target.value })} data-testid="inad-dias-max" />
              </div>
              <div className="w-40">
                <Label>Status contratual</Label>
                <Select value={inadFilters.status_contratual || "todos"} onValueChange={v => setInadFilters({ ...inadFilters, status_contratual: v === "todos" ? "" : v })}>
                  <SelectTrigger data-testid="inad-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => setInadFilters({ q: "", plano_id: "", etapa: "", dias_min: "", dias_max: "", status_contratual: "" })} data-testid="inad-limpar-filtros">
                <Filter size={14} /> Limpar filtros
              </Button>
            </div>
          </Card>

          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-600">
                  <tr>
                    <th className="py-3 px-4">Contrato</th><th>Empresa</th><th>Plano</th><th>Vencimento</th>
                    <th>Dias em atraso</th><th>Valor vencido</th><th>Etapa atual</th><th>Última notificação</th>
                    <th>Próxima ação</th><th className="text-right pr-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {inadimplencia.linhas.map(l => (
                    <tr key={l.company_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`inad-linha-${l.company_id}`}>
                      <td className="py-3 px-4 font-mono text-xs">{l.contract_number_display}</td>
                      <td className="font-medium">{l.empresa_nome}</td>
                      <td>{l.plano_nome}</td>
                      <td>{fmtDate(l.vencimento)}</td>
                      <td>{l.dias_atraso} dia(s)</td>
                      <td>{money(l.valor_vencido)}</td>
                      <td><Badge className={DELINQUENCY_NEXT_ACTION_TONE(l.etapa)}>{l.etapa_label}</Badge></td>
                      <td className="text-xs text-slate-500">
                        {l.ultima_notificacao ? `${OVERDUE_TIPO_LABEL[l.ultima_notificacao.tipo] || l.ultima_notificacao.tipo} enviado em ${fmtDate(l.ultima_notificacao.sent_at)}` : "-"}
                      </td>
                      <td className="text-xs text-slate-600">{l.proxima_acao}</td>
                      <td className="text-right pr-4">
                        <Button size="sm" variant="ghost" onClick={() => openDetail(l.company_id)} data-testid={`inad-revisar-${l.company_id}`}>
                          <Eye size={14} /> Revisar
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {inadimplencia.linhas.length === 0 && (
                    <tr><td colSpan={10} className="py-8 text-center text-slate-400">Nenhuma empresa em atraso</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="bancos" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500">Provedores bancários/de pagamento da própria {PLATFORM_COMPANY_SHORT_NAME}/LOOTUS, usados para cobrar as empresas clientes (assinatura e boleto SaaS). Não confundir com os bancos de cada empresa cliente (Configurações → Bancos), usados para cobrar os PRÓPRIOS clientes dela.</p>
          <div className="flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNewBank} data-testid="banco-novo"><Plus size={16} /> Novo provedor</Button>
          </div>
          {banks.length > 0 && !banks.some(b => b.principal) && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center gap-2" data-testid="banco-sem-principal-aviso">
              <AlertTriangle size={16} className="shrink-0" />
              Nenhum provedor está definido como principal — "Gerar boleto" ficará indisponível até que um provedor ativo com credencial configurada seja definido como principal.
            </div>
          )}
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr><th className="py-3 px-4">Nome</th><th>Provedor</th><th>Ambiente</th><th>Status</th><th>Conexão</th><th className="text-right pr-4">Ações</th></tr>
                </thead>
                <tbody>
                  {banks.map(b => (
                    <tr key={b.id} className="border-t border-slate-100">
                      <td className="py-3 px-4 font-medium text-slate-900 flex items-center gap-1.5">
                        {b.principal ? <Star size={14} className="text-amber-500 fill-amber-500" title="Provedor principal" /> : <Star size={14} className="text-slate-300" title="Não é o provedor principal" />}
                        {b.nome}
                      </td>
                      <td className="text-slate-500">{b.provider === "mercado_pago" ? "Mercado Pago" : b.provider === "asaas" ? "Asaas" : b.provider}</td>
                      <td className="text-slate-500 capitalize">{b.environment}</td>
                      <td>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={b.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>{b.active ? "Ativo" : "Inativo"}</Badge>
                          <Badge className="bg-slate-100 text-slate-600">{b.credential_configured ? "Credencial configurada" : "Sem credencial"}</Badge>
                          {/* Segurança Pré-Produção, Pacote 2 (P1-3/item 12) — só
                              Asaas tem webhook_secret por provedor (Mercado Pago
                              Master usa MERCADOPAGO_WEBHOOK_SECRET de ambiente). */}
                          {b.provider === "asaas" && (
                            <Badge className={b.webhook_secret_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"} title="Sem isto, eventos automáticos de pagamento (webhook) não são processados">
                              Webhook {b.webhook_secret_configured ? "configurado" : "não configurado"}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td><Badge className={BANK_CONNECTION_TONE[b.status_conexao] || "bg-slate-100 text-slate-600"}>{BANK_CONNECTION_LABEL[b.status_conexao] || b.status_conexao}</Badge></td>
                      <td className="text-right pr-4 space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => testBankConnection(b)} disabled={bankTesting === b.id || !b.credential_configured} title="Testar conexão" data-testid={`banco-testar-${b.id}`}><Wifi size={16} /></Button>
                        {b.principal ? (
                          <Button size="sm" variant="ghost" onClick={() => unsetPrincipalBank(b)} title="Remover como principal" data-testid={`banco-principal-${b.id}`}>
                            <Star size={16} className="text-amber-500 fill-amber-500" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setPrincipalBank(b)} disabled={!b.active || !b.credential_configured}
                            title={b.active && b.credential_configured ? "Definir como principal" : "Ative o provedor e configure a credencial antes de defini-lo como principal"}
                            data-testid={`banco-principal-${b.id}`}>
                            <Star size={16} />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openEditBank(b)} title="Editar" data-testid={`banco-editar-${b.id}`}><Pencil size={16} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => toggleBankActive(b)} title={b.active ? "Inativar" : "Ativar"} data-testid={`banco-toggle-${b.id}`}>{b.active ? <Ban size={16} /> : <PlayCircle size={16} />}</Button>
                      </td>
                    </tr>
                  ))}
                  {banks.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">Nenhum provedor bancário cadastrado</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
          <Dialog open={bankOpen} onOpenChange={setBankOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{bankEditing ? "Editar provedor" : "Novo provedor bancário"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome</Label><Input value={bankForm.nome} onChange={e => setBankForm({ ...bankForm, nome: e.target.value })} placeholder={bankForm.provider === "asaas" ? "Ex.: Asaas — Codexpy" : "Ex.: Mercado Pago — Produção"} data-testid="banco-form-nome" /></div>
                <div>
                  <Label>Provedor</Label>
                  <Select value={bankForm.provider} onValueChange={v => setBankForm({ ...bankForm, provider: v })} disabled={!!bankEditing}>
                    <SelectTrigger data-testid="banco-form-provider"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="mercado_pago">Mercado Pago</SelectItem><SelectItem value="asaas">Asaas</SelectItem></SelectContent>
                  </Select>
                  {bankEditing && <p className="text-xs text-slate-500 mt-1">O provedor não pode ser alterado depois de criado.</p>}
                </div>
                <div>
                  <Label>Ambiente</Label>
                  <Select value={bankForm.environment} onValueChange={v => setBankForm({ ...bankForm, environment: v })}>
                    <SelectTrigger data-testid="banco-form-environment"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sandbox">Sandbox</SelectItem><SelectItem value="production">Produção</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{bankForm.provider === "asaas" ? "Chave de API" : "Access Token"} {bankEditing?.credential_configured && "(deixe em branco para manter a credencial atual)"}</Label>
                  <Input type="password" value={bankForm.access_token} onChange={e => setBankForm({ ...bankForm, access_token: e.target.value })} placeholder={bankEditing?.credential_configured ? "••••••••" : (bankForm.provider === "asaas" ? "Chave de API do Asaas" : "Access Token do Mercado Pago")} data-testid="banco-form-token" />
                </div>
                {bankForm.provider === "asaas" && (
                  <div>
                    <Label>Segredo/token do Webhook {bankEditing?.webhook_secret_configured && "(deixe em branco para manter o atual)"}</Label>
                    <Input type="password" value={bankForm.webhook_secret} onChange={e => setBankForm({ ...bankForm, webhook_secret: e.target.value })} placeholder={bankEditing?.webhook_secret_configured ? "••••••••" : "Token enviado pelo Asaas no header asaas-access-token"} data-testid="banco-form-webhook-secret" />
                    <p className="text-xs text-slate-500 mt-1">
                      Eventos automáticos de pagamento (baixa/confirmação de mensalidade SaaS) só são processados com este
                      segredo configurado corretamente — sem ele, o webhook é rejeitado. Não afeta a geração de boleto/PIX.
                    </p>
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={bankForm.active} onChange={e => setBankForm({ ...bankForm, active: e.target.checked })} /> Ativo
                </label>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setBankOpen(false)}>Cancelar</Button><Button onClick={saveBank} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="banco-form-salvar">Salvar</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          {/* PIX da Altaforja (PIX por chave) — QR estático gerado 100%
              pela LOOTUS, exclusivo para a mensalidade das empresas
              clientes. Painel separado (cor própria), nunca misturado à
              lista de provedores Mercado Pago acima nem confundido com a
              chave de um tenant. */}
          <div className="pt-2 border-t border-slate-200" />
          <Card className="p-0 overflow-hidden border-violet-200" data-testid="pix-altaforja-card">
            <div className="p-4 flex justify-between items-start gap-3 border-b border-violet-100 bg-violet-50/60">
              <div>
                <div className="font-heading font-semibold flex items-center gap-2"><QrCode size={16} className="text-violet-600" /> PIX da {PLATFORM_COMPANY_SHORT_NAME} (PIX por chave)</div>
                <p className="text-sm text-slate-600 mt-0.5 max-w-2xl">
                  Chave Pix própria da {PLATFORM_COMPANY_SHORT_NAME}/LOOTUS, usada exclusivamente para cobrar a mensalidade das empresas clientes — nunca a chave de um tenant.
                  Não exige integração bancária. A confirmação do pagamento é manual ou por conciliação. No PIX integrado, o status pode ser atualizado automaticamente pelo provedor configurado.
                </p>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700 shrink-0" onClick={openNewPixKeyAccount} data-testid="nova-chave-pix-altaforja"><Plus size={16} /> Nova chave</Button>
            </div>
            <div className="p-4 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {pixKeyAccounts.length === 0 && (
                <div className="col-span-full py-6 text-center text-slate-400 text-sm">Nenhuma chave Pix da {PLATFORM_COMPANY_SHORT_NAME} configurada ainda.</div>
              )}
              {pixKeyAccounts.map(acc => (
                <div key={acc.id} className="border border-slate-200 rounded-lg p-4" data-testid={`chave-pix-altaforja-${acc.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{acc.name}</div>
                      <div className="text-xs text-slate-500">{acc.institution_name ? `${acc.institution_name} — ` : ""}{acc.holder_name}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {acc.is_default && <Badge className="bg-indigo-100 text-indigo-800">Padrão</Badge>}
                      {!acc.active && <Badge className="bg-slate-200 text-slate-600">Desativada</Badge>}
                    </div>
                  </div>
                  <div className="mt-2 text-sm space-y-0.5">
                    <div><span className="text-slate-500">{PIX_KEY_TYPE_LABELS[acc.key_type] || acc.key_type}:</span> <span className="font-mono">{acc.pix_key_masked}</span></div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditPixKeyAccount(acc)} data-testid={`editar-chave-pix-altaforja-${acc.id}`}><Pencil size={14} /> Editar</Button>
                    <Button size="sm" variant="outline" onClick={() => revealPixKey(acc)} disabled={pkaRevealing === acc.id} data-testid={`ver-chave-pix-altaforja-${acc.id}`}>
                      <Eye size={14} /> {pkaRevealing === acc.id ? "Buscando..." : "Ver chave"}
                    </Button>
                    {acc.active && !acc.is_default && (
                      <Button size="sm" variant="outline" onClick={() => setDefaultPixKeyAccount(acc)} data-testid={`definir-padrao-chave-altaforja-${acc.id}`}>Definir como padrão</Button>
                    )}
                    {acc.is_default && (
                      <Button size="sm" variant="outline" onClick={() => unsetDefaultPixKeyAccount(acc)}>Remover como padrão</Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => togglePixKeyAccountActive(acc)} data-testid={`toggle-ativo-chave-altaforja-${acc.id}`}>
                      {acc.active ? "Desativar" : "Ativar"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Dialog open={pkaOpen} onOpenChange={setPkaOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{pkaEditing ? `Editar chave Pix da ${PLATFORM_COMPANY_SHORT_NAME}` : `Nova chave Pix da ${PLATFORM_COMPANY_SHORT_NAME}`}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome da configuração</Label><Input value={pkaForm.name} onChange={e => setPkaForm({ ...pkaForm, name: e.target.value })} placeholder="Ex.: Chave principal" data-testid="chave-pix-altaforja-form-nome" /></div>
                <div><Label>Banco/instituição (opcional — apenas informativo)</Label><Input value={pkaForm.institution_name} onChange={e => setPkaForm({ ...pkaForm, institution_name: e.target.value })} placeholder="Ex.: Banco XP" /></div>
                <div><Label>Titular da conta</Label><Input value={pkaForm.holder_name} onChange={e => setPkaForm({ ...pkaForm, holder_name: e.target.value })} data-testid="chave-pix-altaforja-form-titular" /></div>
                <div>
                  <Label>Tipo de chave</Label>
                  <Select value={pkaForm.key_type} onValueChange={v => setPkaForm({ ...pkaForm, key_type: v })}>
                    <SelectTrigger data-testid="chave-pix-altaforja-form-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="telefone">Telefone</SelectItem>
                      <SelectItem value="aleatoria">Chave aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Chave Pix</Label>
                  <div className="flex gap-2">
                    <Input type={pkaShowKey ? "text" : "password"} value={pkaForm.pix_key} onChange={e => setPkaForm({ ...pkaForm, pix_key: e.target.value })} placeholder="Cole aqui a chave Pix" data-testid="chave-pix-altaforja-form-chave" />
                    <Button type="button" variant="outline" size="icon" onClick={() => setPkaShowKey(v => !v)} title={pkaShowKey ? "Ocultar" : "Mostrar"}>
                      {pkaShowKey ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={pkaForm.active} onChange={e => setPkaForm({ ...pkaForm, active: e.target.checked })} /> Ativo
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPkaOpen(false)}>Cancelar</Button>
                <Button onClick={savePixKeyAccount} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="salvar-chave-pix-altaforja">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={!!pkaRevealOpen} onOpenChange={v => !v && setPkaRevealOpen(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Chave Pix</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <div className="text-xs text-slate-500">{PIX_KEY_TYPE_LABELS[pkaRevealOpen?.key_type] || pkaRevealOpen?.key_type}</div>
                <div className="font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-3 break-all" data-testid="chave-pix-altaforja-revelada">{pkaRevealOpen?.pix_key}</div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setPkaRevealOpen(null)}>Fechar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="config-cobranca" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500">
            Percentuais globais de multa e juros aplicados nas cobranças SaaS da LOOTUS. Nunca são definidos por cobrança —
            valem para toda reemissão de boleto/PIX em atraso (item B/C). Alteráveis apenas pelo Admin Master.
          </p>
          <Card className="p-4 max-w-lg space-y-3">
            <div>
              <Label>Multa (%) — política comercial da LOOTUS: até 2%</Label>
              <Input type="number" min="0" max="2" step="0.01" value={billingConfigForm.multa_percentual}
                onChange={e => setBillingConfigForm({ ...billingConfigForm, multa_percentual: e.target.value })} data-testid="config-cobranca-multa" />
            </div>
            <div>
              <Label>Juros ao mês (%) — política comercial da LOOTUS: até 1% a.m.</Label>
              <Input type="number" min="0" max="1" step="0.01" value={billingConfigForm.juros_mensal_percentual}
                onChange={e => setBillingConfigForm({ ...billingConfigForm, juros_mensal_percentual: e.target.value })} data-testid="config-cobranca-juros" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={billingConfigForm.aplicar_encargos_reemissao_default}
                onChange={e => setBillingConfigForm({ ...billingConfigForm, aplicar_encargos_reemissao_default: e.target.checked })}
                data-testid="config-cobranca-default-encargos" />
              Aplicar juros e multa automaticamente na reemissão (padrão sugerido — o admin pode desmarcar em cada reemissão)
            </label>
            <div>
              {/* Pacote consolidado pós-homologação, Prioridade 15 (e
                  Correções de homologação, P4) — número usado pelo botão
                  "Enviar comprovante pelo WhatsApp" no Financeiro do
                  cliente (por cobrança e no banner/modal de bloqueio);
                  nunca hardcoded no frontend. */}
              <Label>WhatsApp do financeiro (botão "Enviar comprovante pelo WhatsApp" no Financeiro do cliente)</Label>
              <Input placeholder="(11) 91234-5678" value={billingConfigForm.whatsapp_financeiro_numero}
                onChange={e => setBillingConfigForm({ ...billingConfigForm, whatsapp_financeiro_numero: e.target.value })} data-testid="config-cobranca-whatsapp-financeiro" />
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
              Fórmula: <code>multa = valor_base × multa% </code>; <code>juros = valor_base × juros_mensal% × dias_atraso / 30</code>;{" "}
              <code>novo_valor = valor_base + multa + juros</code>. Nunca há juros sobre juros: o valor_base usado é sempre o
              valor original da cobrança, nunca um valor já reemitido.
              <br /><br />
              Exemplo com a política padrão (multa 2% + juros 1% a.m.) sobre uma mensalidade de R$199,00 vencida há 15 dias:
              multa R$3,98 + juros R$1,00 = novo valor R$203,98.
            </div>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveBillingConfig} data-testid="config-cobranca-salvar">Salvar</Button>
          </Card>

          {/* Configuração Central de E-mail de Cobrança — identidade e SMTP
              da PRÓPRIA Codexpy (nunca de um tenant). Trocar remetente/SMTP
              aqui vale para o PRÓXIMO envio, sem restart/deploy. */}
          <Card className="p-4 max-w-lg space-y-3" data-testid="config-email-cobranca">
            <div className="flex items-center justify-between">
              <div className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Mail size={16} /> E-mail de cobrança</div>
              {emailSettings?.password_configured ? (
                <Badge className="bg-emerald-100 text-emerald-800">Configurado</Badge>
              ) : (
                <Badge className="bg-slate-200 text-slate-700">Não configurado</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500">
              Identidade e conexão SMTP usadas para enviar toda cobrança SaaS (e-mail individual, automático e em lote) —
              configuração global da Codexpy, nunca por tenant. Trocar aqui vale a partir do próximo envio, sem reiniciar nada.
            </p>

            {!emailSettingsEditing && (
              <div className="space-y-2">
                <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
                  <div><span className="text-slate-500">Remetente:</span>{" "}
                    <span className="font-medium text-slate-900">
                      {emailSettings?.from_name || "-"} {emailSettings?.from_email ? `<${emailSettings.from_email}>` : ""}
                    </span>
                  </div>
                  <div><span className="text-slate-500">Reply-To:</span> <span className="font-medium text-slate-900">{emailSettings?.reply_to || "-"}</span></div>
                  <div><span className="text-slate-500">SMTP:</span> <span className="font-medium text-slate-900">{emailSettings?.smtp_host ? `${emailSettings.smtp_host}:${emailSettings.smtp_port}` : "-"}</span></div>
                  <div><span className="text-slate-500">Segurança:</span> <span className="font-medium text-slate-900">{SMTP_SECURITY_LABEL[emailSettings?.security] || "-"}</span></div>
                  <div><span className="text-slate-500">Usuário SMTP:</span> <span className="font-medium text-slate-900">{emailSettings?.smtp_username || "-"}</span></div>
                  <div><span className="text-slate-500">Credencial:</span> <span className="font-medium text-slate-900">{emailSettings?.password_configured ? "Configurada" : "Não configurada"}</span></div>
                  {emailSettings?.last_test_status && (
                    <div className="flex items-center gap-1.5 pt-1">
                      {emailSettings.last_test_status === "success" ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-red-600" />}
                      <span className="text-xs text-slate-500">
                        Último teste ({emailSettings.last_test_status === "success" ? "sucesso" : "falha"}
                        {emailSettings.last_test_at ? `, ${new Date(emailSettings.last_test_at).toLocaleString("pt-BR")}` : ""}): {emailSettings.last_test_detail}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEmailSettingsEditing(true)} data-testid="config-email-editar">
                    <Pencil size={14} /> {emailSettings?.password_configured ? "Configurar" : "Configurar e-mail de cobrança"}
                  </Button>
                  {emailSettings?.password_configured && (
                    <Button size="sm" variant="outline" disabled={testingEmailConnection} onClick={testEmailConnection} data-testid="config-email-testar-conexao">
                      <Wifi size={14} /> {testingEmailConnection ? "Testando..." : "Testar conexão"}
                    </Button>
                  )}
                </div>
                {emailSettings?.password_configured && (
                  <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
                    <div className="flex-1 min-w-[200px]">
                      <Label>Enviar e-mail de teste para</Label>
                      <Input placeholder="seu-email@exemplo.com" value={testEmailTo} onChange={e => setTestEmailTo(e.target.value)} data-testid="config-email-teste-destino" />
                    </div>
                    <Button size="sm" variant="outline" disabled={sendingTestEmail} onClick={sendTestEmail} data-testid="config-email-enviar-teste">
                      <Mail size={14} /> {sendingTestEmail ? "Enviando..." : "Enviar e-mail de teste"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {emailSettingsEditing && (
              <div className="space-y-3">
                <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Identidade</div>
                <div>
                  <Label>Nome do remetente</Label>
                  <Input value={emailSettingsForm.from_name} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, from_name: e.target.value })} placeholder="Codexpy Financeiro" data-testid="config-email-from-name" />
                </div>
                <div>
                  <Label>E-mail do remetente</Label>
                  <Input value={emailSettingsForm.from_email} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, from_email: e.target.value })} placeholder="financeiro@codexpy.com.br" data-testid="config-email-from-email" />
                </div>
                <div>
                  <Label>Reply-To</Label>
                  <Input value={emailSettingsForm.reply_to} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, reply_to: e.target.value })} placeholder="financeiro@codexpy.com.br" data-testid="config-email-reply-to" />
                </div>

                <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold pt-2">Conexão SMTP</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2 sm:col-span-1">
                    <Label>SMTP Host</Label>
                    <Input value={emailSettingsForm.smtp_host} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_host: e.target.value })} placeholder="smtp.hostinger.com" data-testid="config-email-smtp-host" />
                  </div>
                  <div>
                    <Label>SMTP Port</Label>
                    <Input type="number" value={emailSettingsForm.smtp_port} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_port: e.target.value })} placeholder="465" data-testid="config-email-smtp-port" />
                  </div>
                </div>
                <div>
                  <Label>Segurança</Label>
                  <Select value={emailSettingsForm.security} onValueChange={v => setEmailSettingsForm({ ...emailSettingsForm, security: v })}>
                    <SelectTrigger data-testid="config-email-security"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ssl_tls">SSL/TLS</SelectItem>
                      <SelectItem value="starttls">STARTTLS</SelectItem>
                      <SelectItem value="none">Sem TLS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  {/* Item 2 do pedido — identidade ≠ autenticação: o
                      usuário SMTP pode ser uma mailbox diferente do
                      remetente visível, quando financeiro@ é um alias. */}
                  <Label>Usuário SMTP <span className="font-normal text-slate-400">(mailbox real autorizada — pode ser diferente do e-mail do remetente, se este for um alias)</span></Label>
                  <Input value={emailSettingsForm.smtp_username} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_username: e.target.value })} placeholder="ramile@codexpy.com.br" data-testid="config-email-smtp-username" />
                </div>
                <div>
                  <Label>Senha SMTP</Label>
                  <Input type="password" value={emailSettingsForm.smtp_password} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, smtp_password: e.target.value })}
                    placeholder={emailSettings?.password_configured ? "Senha já configurada. Preencha somente para substituir." : "Digite a senha SMTP"} data-testid="config-email-smtp-password" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={emailSettingsForm.active} onChange={e => setEmailSettingsForm({ ...emailSettingsForm, active: e.target.checked })} data-testid="config-email-ativo" />
                  Ativo (quando desmarcado, a plataforma cai para a configuração de fallback do ambiente, se houver)
                </label>
                <div className="flex gap-2 pt-2">
                  <Button className="bg-blue-600 hover:bg-blue-700" onClick={saveEmailSettings} data-testid="config-email-salvar">Salvar</Button>
                  <Button variant="outline" onClick={() => { setEmailSettingsEditing(false); loadEmailSettings(); }} data-testid="config-email-cancelar">Cancelar</Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="historico" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500">Todas as alterações comerciais desta empresa — plano, mensalidade, desconto, reajuste, vencimento, suspensão/retomada, faturamento, pagamento e cancelamento. A condição comercial vigente (aba Empresas → Ver condição) mostra só o estado atual, sem essa listagem crescente.</p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-64">
              <Label>Empresa</Label>
              <Select value={historyFilters.company_id || "todas"} onValueChange={v => setHistoryFilters({ ...historyFilters, company_id: v === "todas" ? "" : v })}>
                <SelectTrigger data-testid="historico-empresa"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as empresas</SelectItem>
                  {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr><th className="py-3 px-4">Quando</th><th>Empresa</th><th>Evento</th><th>Usuário</th><th className="text-right pr-4">Detalhes</th></tr>
                </thead>
                <tbody>
                  {historyEvents.map((e, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-3 px-4 text-slate-500 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString("pt-BR") : "-"}</td>
                      <td className="text-slate-500">{e.empresa_nome}</td>
                      <td className="font-medium text-slate-900">{e.op_label}</td>
                      <td className="text-slate-500">{e.user_name || "Sistema"}</td>
                      <td className="text-right pr-4">
                        {(e.before || e.after) && (
                          <Button size="sm" variant="ghost" onClick={() => setHistoryDetailEvent(e)} data-testid={`historico-detalhes-${i}`}><Eye size={16} /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {historyEvents.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nenhum evento encontrado</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detalhe da condição comercial de uma empresa (item 16) */}
      <Dialog open={!!detailId} onOpenChange={v => { if (!v) closeDetail(); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{detail ? "Condição comercial" : "Carregando..."}</DialogTitle></DialogHeader>
          {detail && (
            <div className="max-h-[75vh] overflow-y-auto pr-1 space-y-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Plano</div>
                  <div className="font-semibold text-slate-900">{detail.plano?.nome || "Sem plano"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Mensalidade atual</div>
                  <div className="font-semibold text-slate-900">{money(detail.valor_base)}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Desconto vigente</div>
                  <div className="font-semibold text-slate-900">
                    {detail.desconto_valor > 0
                      ? (detail.desconto_tipo === "percentual" ? `${detail.desconto_valor}%` : money(detail.desconto_valor))
                      : "Nenhum"}
                  </div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Valor efetivo</div>
                  <div className="font-semibold text-slate-900">{money(detail.valor_mensal_efetivo)}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Data de adesão</div>
                  <div className="font-semibold text-slate-900">{detail.data_adesao ? fmtDate(detail.data_adesao) : "-"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  {/* Finalização do Ciclo de Faturamento SaaS Master, item
                      1/12 — o dia de vencimento NUNCA é mais um campo
                      editável à parte: é sempre o dia da adesão acima. */}
                  <div className="text-xs text-slate-500">Vencimento mensal</div>
                  <div className="font-semibold text-slate-900">{detail.vencimento_mensal_dia ? `Todo dia ${detail.vencimento_mensal_dia}` : "-"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Próxima emissão</div>
                  <div className="font-semibold text-slate-900">{detail.term.cobranca_ativa && detail.proxima_emissao ? fmtDate(detail.proxima_emissao) : "-"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Próximo vencimento</div>
                  <div className="font-semibold text-slate-900">{detail.term.cobranca_ativa && detail.proximo_vencimento ? fmtDate(detail.proximo_vencimento) : "-"}</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Próximo reajuste</div>
                  <div className="font-semibold text-slate-900">
                    {detail.term.reajuste_programado
                      ? `${money(detail.term.reajuste_programado.novo_valor)} a partir de ${fmtDate(detail.term.reajuste_programado.vigencia)}`
                      : "Nenhum"}
                  </div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="text-xs text-slate-500">Cobrança</div>
                  <Badge className={detail.term.cobranca_ativa ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{detail.term.cobranca_ativa ? "Ativa" : "Suspensa"}</Badge>
                </div>
              </div>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Condição comercial</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Data de adesão</Label><Input type="date" value={commercialForm.data_adesao} onChange={e => setCommercialForm({ ...commercialForm, data_adesao: e.target.value })} data-testid="comercial-data-adesao" /></div>
                  <div><Label>Mensalidade específica (R$)</Label><Input type="number" step="0.01" placeholder={`Herda do plano — ${money((detail.plano || {}).valor_mensal_padrao)}`} value={commercialForm.valor_mensal} onChange={e => setCommercialForm({ ...commercialForm, valor_mensal: e.target.value })} data-testid="comercial-valor-mensal" /></div>
                  <div><Label>Adesão específica (R$)</Label><Input type="number" step="0.01" placeholder={`Herda do plano — ${money((detail.plano || {}).valor_adesao_padrao)}`} value={commercialForm.valor_adesao} onChange={e => setCommercialForm({ ...commercialForm, valor_adesao: e.target.value })} data-testid="comercial-valor-adesao" /></div>
                  <div className="md:col-span-2"><Label>Observação comercial</Label><Textarea value={commercialForm.observacao} onChange={e => setCommercialForm({ ...commercialForm, observacao: e.target.value })} data-testid="comercial-observacao" /></div>
                </div>
                <Button type="button" variant="outline" className="mt-2" onClick={saveCommercial} data-testid="comercial-salvar">Salvar condição comercial</Button>
              </section>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2"><Tag size={16} /> Desconto</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={discountForm.tipo || "nenhum"} onValueChange={v => setDiscountForm({ ...discountForm, tipo: v === "nenhum" ? "" : v })}>
                      <SelectTrigger data-testid="desconto-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nenhum">Sem desconto</SelectItem>
                        <SelectItem value="fixo">Valor fixo</SelectItem>
                        <SelectItem value="percentual">Percentual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {discountForm.tipo && (
                    <>
                      <div><Label>Valor</Label><Input type="number" step="0.01" value={discountForm.valor} onChange={e => setDiscountForm({ ...discountForm, valor: e.target.value })} data-testid="desconto-valor" /></div>
                      <div><Label>Início</Label><Input type="date" value={discountForm.inicio || ""} onChange={e => setDiscountForm({ ...discountForm, inicio: e.target.value })} data-testid="desconto-inicio" /></div>
                      <div><Label>Término (opcional)</Label><Input type="date" value={discountForm.termino || ""} onChange={e => setDiscountForm({ ...discountForm, termino: e.target.value })} data-testid="desconto-termino" /></div>
                      <div className="col-span-2 md:col-span-4"><Label>Motivo</Label><Input value={discountForm.motivo || ""} onChange={e => setDiscountForm({ ...discountForm, motivo: e.target.value })} data-testid="desconto-motivo" /></div>
                    </>
                  )}
                </div>
                <div className="mt-2 flex gap-2">
                  <Button type="button" variant="outline" onClick={saveDiscount} data-testid="desconto-salvar">Salvar desconto</Button>
                  {detail.term.desconto && <Button type="button" variant="ghost" onClick={clearDiscount} data-testid="desconto-remover">Remover desconto</Button>}
                </div>
              </section>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Reajuste programado</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label>Novo valor (R$)</Label><Input type="number" step="0.01" value={adjustmentForm.novo_valor} onChange={e => setAdjustmentForm({ ...adjustmentForm, novo_valor: e.target.value })} data-testid="reajuste-valor" /></div>
                  <div><Label>Vigência</Label><Input type="date" value={adjustmentForm.vigencia || ""} onChange={e => setAdjustmentForm({ ...adjustmentForm, vigencia: e.target.value })} data-testid="reajuste-vigencia" /></div>
                  <div><Label>Motivo</Label><Input value={adjustmentForm.motivo || ""} onChange={e => setAdjustmentForm({ ...adjustmentForm, motivo: e.target.value })} data-testid="reajuste-motivo" /></div>
                </div>
                <p className="text-xs text-slate-500 mt-1">Até a vigência, cobranças continuam usando o valor atual; a partir dela, o novo valor passa a ser usado automaticamente.</p>
                <div className="mt-2 flex gap-2">
                  <Button type="button" variant="outline" onClick={saveAdjustment} data-testid="reajuste-salvar">Programar reajuste</Button>
                  {detail.term.reajuste_programado && <Button type="button" variant="ghost" onClick={clearAdjustment} data-testid="reajuste-cancelar">Cancelar reajuste</Button>}
                </div>
              </section>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2"><CreditCard size={16} /> Assinatura Mercado Pago (legado)</div>
                <p className="text-xs text-slate-500 mb-2">
                  Fluxo original (Fase 1) — abre um checkout que aceita cartão. O fluxo comercial PADRÃO da cobrança SaaS
                  é Boleto/PIX (aba Faturamento SaaS, sem cartão). Mantido apenas por compatibilidade — não é a forma
                  recomendada de cobrar.
                </p>
                {!subscription?.mercadopago_configured ? (
                  <p className="text-sm text-slate-500">Mercado Pago não está configurado neste ambiente.</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge className={SUBSCRIPTION_STATUS_TONE[subscription?.status] || "bg-slate-100 text-slate-600"}>
                        {SUBSCRIPTION_STATUS_LABEL[subscription?.status] || subscription?.status || "Não iniciada"}
                      </Badge>
                      {subscription?.subscription?.grace_until && (
                        <span className="text-xs text-amber-700">tolerância até {fmtDate(subscription.subscription.grace_until)}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(!subscription?.subscription || ["cancelled", "failed"].includes(subscription?.status)) && (
                        <Button size="sm" variant="outline" onClick={startSubscription} data-testid="mp-subscription-start"><Plus size={14} /> Iniciar assinatura</Button>
                      )}
                      {subscription?.subscription?.checkout_url && subscription?.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => window.open(subscription.subscription.checkout_url, "_blank", "noopener,noreferrer")} data-testid="mp-subscription-checkout"><ExternalLink size={14} /> Abrir checkout</Button>
                      )}
                      {subscription?.subscription && !["cancelled"].includes(subscription?.status) && (
                        <Button size="sm" variant="outline" onClick={cancelSubscription} data-testid="mp-subscription-cancel"><Ban size={14} /> Cancelar</Button>
                      )}
                      {subscription?.subscription && (
                        <Button size="sm" variant="ghost" onClick={syncSubscription} data-testid="mp-subscription-sync"><RefreshCw size={14} /> Sincronizar</Button>
                      )}
                    </div>
                  </div>
                )}
              </section>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Cobrança</div>
                {detail.term.cobranca_ativa ? (
                  <Button type="button" variant="outline" onClick={() => setSuspendOpen(true)} data-testid="cobranca-suspender"><PauseCircle size={16} /> Suspender cobrança</Button>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                      Suspensa em {detail.term.suspensa_em ? fmtDate(detail.term.suspensa_em) : "-"} — motivo: {detail.term.suspensao_motivo}
                      {detail.term.retomada_prevista_em && <> · retomada prevista em {fmtDate(detail.term.retomada_prevista_em)}</>}
                    </div>
                    <Button type="button" className="bg-emerald-600 hover:bg-emerald-700" onClick={resumeBilling} data-testid="cobranca-retomar"><PlayCircle size={16} /> Retomar cobrança</Button>
                  </div>
                )}
              </section>

              {/* Pacote consolidado pós-homologação, Prioridades 13/14/16 —
                  bloqueio financeiro automático (15 dias) e liberação de
                  confiança manual (48h). Domínio SEPARADO da Assinatura
                  Mercado Pago legada acima e da "Cobrança" (suspender/
                  retomar geração de faturamento) — este é o acesso
                  OPERACIONAL do tenant ao próprio LOOTUS. */}
              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Acesso financeiro (bloqueio por inadimplência)</div>
                <div className="space-y-2">
                  {/* Contrato Permanente + Régua de Inadimplência (item 17)
                      — mesma fonte já usada no painel global (aba
                      Inadimplência), nunca um segundo cálculo. */}
                  {detail.inadimplencia && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mb-1">
                      <span className="flex items-center gap-1"><Hash size={12} className="text-slate-400" /> Contrato nº {detail.contract_number_display}</span>
                      <span>Dias em atraso: <b className="text-slate-900">{detail.inadimplencia.dias_atraso || 0}</b></span>
                      <span>Etapa: <b className="text-slate-900">{detail.inadimplencia.etapa_label || "Em dia"}</b></span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    {detail.financeiro?.financially_blocked ? (
                      <Badge className="bg-red-100 text-red-800">Bloqueado por inadimplência</Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800">Acesso normal</Badge>
                    )}
                    {detail.financeiro?.manual_financial_suspension?.active && (
                      <Badge className="bg-red-100 text-red-800" title={detail.financeiro.manual_financial_suspension.motivo}>
                        Suspenso manualmente
                      </Badge>
                    )}
                    {detail.financeiro?.trusted_access_until && new Date(detail.financeiro.trusted_access_until) > new Date() && (
                      <span className="text-xs text-blue-700">liberado por confiança até {fmtDateTimeBr(detail.financeiro.trusted_access_until)}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Suspensão por Inadimplência (item novo) — só a
                        partir de D+10 e só quando ainda não está
                        bloqueada/suspensa (evita uma ação sem efeito). */}
                    {detail.inadimplencia?.dias_atraso >= 10 && !detail.financeiro?.financially_blocked && (
                      <Button type="button" size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={() => setSuspendDelinquencyOpen(true)} data-testid="suspender-inadimplencia-abrir">
                        <Ban size={14} /> Suspender por inadimplência
                      </Button>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={() => setTrustedAccessOpen(true)} data-testid="liberar-confianca-abrir">
                      <CalendarClock size={14} /> Liberar por 2 dias
                    </Button>
                    {detail.financeiro?.trusted_access_until && (
                      <Button type="button" size="sm" variant="ghost" onClick={revokeTrustedAccess} data-testid="liberar-confianca-revogar">
                        <Ban size={14} /> Revogar liberação
                      </Button>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center justify-between">
                  <span>Cobranças desta empresa</span>
                  <Button size="sm" variant="outline" onClick={() => openGenerate(detailId)} data-testid="comercial-gerar-para-empresa"><Plus size={14} /> Gerar título manual</Button>
                </div>
                <ChargesTable list={detailCharges} showActions />
              </section>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Suspender cobrança */}
      <Dialog open={suspendOpen} onOpenChange={setSuspendOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Suspender cobrança</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Motivo *</Label><Textarea value={suspendForm.motivo} onChange={e => setSuspendForm({ ...suspendForm, motivo: e.target.value })} data-testid="suspender-motivo" /></div>
            <div><Label>Retomada prevista (opcional)</Label><Input type="date" value={suspendForm.retomada_prevista} onChange={e => setSuspendForm({ ...suspendForm, retomada_prevista: e.target.value })} data-testid="suspender-retomada" /></div>
            <p className="text-xs text-slate-500">Não apaga plano/recursos/histórico; só impede novas cobranças automáticas. Não bloqueia o acesso ao LOOTUS nesta fase.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendOpen(false)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitSuspend} data-testid="suspender-confirmar">Suspender</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspensão por Inadimplência (delta 2026-09-15) — confirmação
          explícita + motivo obrigatório; o backend SEMPRE revalida
          elegibilidade (D+10) e se a cobrança continua vencida/não paga
          antes de aplicar, nunca confia no que esta tela mostra. */}
      <Dialog open={suspendDelinquencyOpen} onOpenChange={setSuspendDelinquencyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban size={18} className="text-red-600" /> Suspender por inadimplência</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Bloqueia o acesso operacional desta empresa agora, antes do bloqueio automático de 15 dias. Só é possível
              a partir de D+10 e enquanto a cobrança mais antiga ainda estiver vencida e não paga — o backend revalida
              as duas condições no momento da confirmação e recusa a ação se a empresa já tiver regularizado o pagamento.
            </p>
            <div><Label>Motivo *</Label><Textarea value={suspendDelinquencyMotivo} onChange={e => setSuspendDelinquencyMotivo(e.target.value)} data-testid="suspender-inadimplencia-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDelinquencyOpen(false)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={suspendingDelinquency} onClick={suspendDelinquency} data-testid="suspender-inadimplencia-confirmar">
              {suspendingDelinquency ? "Suspendendo..." : "Suspender por inadimplência"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Liberar por 2 dias (Pacote consolidado pós-homologação, Prioridade
          16) — SEMPRE 48h fixas (nunca configurável aqui), sempre com
          motivo, sempre auditado. NUNCA confundir com o prazo de
          tolerância normal de 15 dias — esta é uma decisão manual. */}
      <Dialog open={trustedAccessOpen} onOpenChange={setTrustedAccessOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Liberar acesso por confiança (48h)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Libera o acesso operacional desta empresa por 48 horas, mesmo sem confirmação de pagamento ainda.
              Se o pagamento (ou uma baixa manual) acontecer dentro desse prazo, o acesso segue liberado normalmente;
              caso contrário, o bloqueio automático retorna ao final das 48h.
            </p>
            <div><Label>Motivo *</Label><Textarea value={trustedAccessMotivo} onChange={e => setTrustedAccessMotivo(e.target.value)} data-testid="liberar-confianca-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrustedAccessOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={grantTrustedAccess} data-testid="liberar-confianca-confirmar">Liberar por 48h</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gerar título manual (item 11) — exceção administrativa; o ciclo
          normal (mensal, no dia da adesão) é AUTOMÁTICO. */}
      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Gerar título manual</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            O ciclo mensal normal é gerado automaticamente (mesmo dia da adesão da empresa) — use isto só para uma
            exceção administrativa (competência retroativa ou antecipada fora do ciclo padrão).
          </p>
          <div className="space-y-3">
            <div>
              <Label>Empresa</Label>
              <Select value={generateForm.company_id} onValueChange={v => setGenerateForm({ ...generateForm, company_id: v })}>
                <SelectTrigger data-testid="gerar-empresa"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Competência (opcional, padrão mês corrente)</Label>
              <Input
                placeholder="MM-AAAA"
                maxLength={7}
                value={generateForm.competencia}
                onChange={e => setGenerateForm({ ...generateForm, competencia: maskCompetencia(e.target.value) })}
                data-testid="gerar-competencia"
              />
              <p className="text-xs text-slate-400 mt-1">É possível faturar uma competência futura (faturamento antecipado) — ex.: em 08-2026, faturar 09-2026.</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={generateForm.force} onChange={e => setGenerateForm({ ...generateForm, force: e.target.checked })} /> Gerar mesmo se já existir uma cobrança nesta competência
            </label>
            {generateSuspended && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                <div className="flex items-start gap-2 text-amber-800 text-sm font-medium">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  Esta empresa está com a cobrança suspensa. Deseja gerar este faturamento excepcionalmente?
                </div>
                <div>
                  <Label>Motivo da exceção *</Label>
                  <Textarea
                    value={generateForm.override_motivo}
                    onChange={e => setGenerateForm({ ...generateForm, override_motivo: e.target.value })}
                    placeholder="Explique por que este faturamento excepcional é necessário"
                    data-testid="gerar-override-motivo"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitGenerate} data-testid="gerar-confirmar">Gerar título manual</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Baixar cobrança */}
      <Dialog open={!!payTarget} onOpenChange={v => !v && setPayTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Baixar cobrança — {payTarget?.empresa_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {payTarget?.boleto_gerado && isRealInstrumentMode(payTarget?.boleto_modo) && ["waiting_payment", "processing"].includes(payTarget?.boleto_status) && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
                Esta cobrança tem um {payTarget?.instrumento_tipo === "pix" ? "PIX" : "boleto"} real do {platformProviderLabel(payTarget?.boleto_modo)} ainda aguardando confirmação.
                Esta é uma <strong>baixa manual administrativa</strong> — ela NÃO altera a situação no {platformProviderLabel(payTarget?.boleto_modo)}. Descreva o motivo na observação (obrigatório).
              </div>
            )}
            <div><Label>Data do pagamento *</Label><Input type="date" value={payForm.data_pagamento} onChange={e => setPayForm({ ...payForm, data_pagamento: e.target.value })} data-testid="baixar-data" /></div>
            <div><Label>Forma de pagamento *</Label><Input value={payForm.forma_pagamento} onChange={e => setPayForm({ ...payForm, forma_pagamento: e.target.value })} placeholder="PIX, transferência, boleto..." data-testid="baixar-forma" /></div>
            <div><Label>Observação</Label><Textarea value={payForm.observacao} onChange={e => setPayForm({ ...payForm, observacao: e.target.value })} data-testid="baixar-observacao" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayTarget(null)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitPay} data-testid="baixar-confirmar">Confirmar baixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anular baixa */}
      <Dialog open={!!undoTarget} onOpenChange={v => !v && setUndoTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Anular baixa — {undoTarget?.empresa_nome}</DialogTitle></DialogHeader>
          <div><Label>Motivo *</Label><Textarea value={undoReason} onChange={e => setUndoReason(e.target.value)} data-testid="anular-motivo" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)}>Cancelar</Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={submitUndo} data-testid="anular-confirmar">Anular baixa</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alterar vencimento de uma cobrança */}
      <Dialog open={!!dueDateTarget} onOpenChange={v => !v && setDueDateTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alterar vencimento — {dueDateTarget?.empresa_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Novo vencimento *</Label><Input type="date" value={dueDateForm.vencimento} onChange={e => setDueDateForm({ ...dueDateForm, vencimento: e.target.value })} data-testid="alterar-vencimento-data" /></div>
            <div><Label>Motivo *</Label><Textarea value={dueDateForm.motivo} onChange={e => setDueDateForm({ ...dueDateForm, motivo: e.target.value })} data-testid="alterar-vencimento-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDueDateTarget(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitDueDate} data-testid="alterar-vencimento-confirmar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancelar cobrança */}
      <Dialog open={!!cancelTarget} onOpenChange={v => !v && setCancelTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar cobrança — {cancelTarget?.empresa_nome}</DialogTitle></DialogHeader>
          <div><Label>Motivo *</Label><Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} data-testid="cancelar-motivo" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>Voltar</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitCancel} data-testid="cancelar-confirmar">Cancelar cobrança</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alterar vencimento / Reemitir boleto ou PIX real (item C) */}
      <Dialog open={!!reemitirTarget} onOpenChange={v => !v && (setReemitirTarget(null), setReemitirPreviewData(null))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alterar vencimento — {reemitirTarget?.empresa_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              O {reemitirTarget?.instrumento_tipo === "pix" ? "PIX" : "boleto"} já emitido não pode ser editado — uma REEMISSÃO cria um novo
              instrumento com o novo vencimento. O anterior é preservado no histórico.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-xs text-slate-500">Vencimento atual</div>
                <div className="font-semibold text-slate-900">{reemitirTarget ? fmtDate(reemitirTarget.vencimento) : "-"}</div>
              </div>
              <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-xs text-slate-500">Valor base</div>
                <div className="font-semibold text-slate-900">{reemitirTarget ? money(reemitirTarget.valor_final) : "-"}</div>
              </div>
            </div>
            <div>
              <Label>Novo vencimento *</Label>
              <Input type="date" value={reemitirForm.novo_vencimento} onChange={e => setReemitirForm({ ...reemitirForm, novo_vencimento: e.target.value })} data-testid="reemitir-vencimento" />
              {reemitirForm.novo_vencimento && [0, 6].includes(new Date(`${reemitirForm.novo_vencimento}T00:00:00`).getDay()) && (
                <p className="text-xs text-amber-600 mt-1">A data informada não é dia útil (fim de semana). O vencimento efetivo poderá ser ajustado pelo banco/provedor.</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={reemitirForm.aplicar_encargos} onChange={e => setReemitirForm({ ...reemitirForm, aplicar_encargos: e.target.checked })} data-testid="reemitir-aplicar-encargos" />
              Aplicar juros e multa (config. global: {billingConfig ? `${billingConfig.multa_percentual}% multa + ${billingConfig.juros_mensal_percentual}% a.m.` : "-"})
            </label>
            <div><Label>Motivo *</Label><Textarea value={reemitirForm.motivo} onChange={e => setReemitirForm({ ...reemitirForm, motivo: e.target.value })} data-testid="reemitir-motivo" /></div>
            {reemitirPreview && (
              <div className="grid grid-cols-2 gap-2 text-sm p-3 rounded-md bg-blue-50 border border-blue-200">
                <div>Dias em atraso: <strong>{reemitirPreview.diasAtraso}</strong></div>
                <div>Multa: <strong>{money(reemitirPreview.multa)}</strong></div>
                <div>Juros: <strong>{money(reemitirPreview.juros)}</strong></div>
                <div>Novo valor total: <strong>{money(reemitirPreview.novoValor)}</strong></div>
                <div className="col-span-2 text-xs text-slate-500">Prévia apenas para conferência — o valor final é sempre calculado pelo servidor.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReemitirTarget(null); setReemitirPreviewData(null); }}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitReemitir} data-testid="reemitir-confirmar">Reemitir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regularizar cobrança vencida sem instrumento (Pacote consolidado
          pós-homologação, Prioridade 4) */}
      <Dialog open={!!regularizarTarget} onOpenChange={v => !v && (setRegularizarTarget(null), setRegularizarPreviewData(null))}>
        <DialogContent>
          <DialogHeader><DialogTitle>Regularizar cobrança vencida — {regularizarTarget?.charge?.empresa_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Esta cobrança está vencida e ainda não tem boleto/PIX. Informe o novo vencimento — juros e multa da política
              global são calculados automaticamente sobre os dias em atraso.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-xs text-slate-500">Vencimento original</div>
                <div className="font-semibold text-slate-900">{regularizarTarget ? fmtDate(regularizarTarget.charge.vencimento) : "-"}</div>
              </div>
              <div className="p-2 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-xs text-slate-500">Valor base</div>
                <div className="font-semibold text-slate-900">{regularizarTarget ? money(regularizarTarget.charge.valor_final) : "-"}</div>
              </div>
            </div>
            <div>
              <Label>Novo vencimento *</Label>
              <Input type="date" value={regularizarForm.novo_vencimento} onChange={e => setRegularizarForm({ ...regularizarForm, novo_vencimento: e.target.value })} data-testid="regularizar-vencimento" />
              {regularizarForm.novo_vencimento && [0, 6].includes(new Date(`${regularizarForm.novo_vencimento}T00:00:00`).getDay()) && (
                <p className="text-xs text-amber-600 mt-1">A data informada não é dia útil (fim de semana). O vencimento efetivo poderá ser ajustado pelo banco/provedor.</p>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={regularizarForm.aplicar_encargos} onChange={e => setRegularizarForm({ ...regularizarForm, aplicar_encargos: e.target.checked })} data-testid="regularizar-aplicar-encargos" />
              Aplicar juros e multa (config. global: {billingConfig ? `${billingConfig.multa_percentual}% multa + ${billingConfig.juros_mensal_percentual}% a.m.` : "-"})
            </label>
            <div><Label>Motivo *</Label><Textarea value={regularizarForm.motivo} onChange={e => setRegularizarForm({ ...regularizarForm, motivo: e.target.value })} data-testid="regularizar-motivo" /></div>
            {regularizarPreview && (
              <div className="grid grid-cols-2 gap-2 text-sm p-3 rounded-md bg-blue-50 border border-blue-200">
                <div>Dias em atraso: <strong>{regularizarPreview.diasAtraso}</strong></div>
                <div>Multa: <strong>{money(regularizarPreview.multa)}</strong></div>
                <div>Juros: <strong>{money(regularizarPreview.juros)}</strong></div>
                <div>Valor final: <strong>{money(regularizarPreview.novoValor)}</strong></div>
                <div className="col-span-2 text-xs text-slate-500">Prévia apenas para conferência — o valor final é sempre calculado pelo servidor.</div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRegularizarTarget(null); setRegularizarPreviewData(null); }}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitRegularizar} data-testid="regularizar-confirmar">
              {regularizarTarget?.tipo === "pix" ? "Gerar PIX" : "Gerar boleto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code do PIX ativo (item G/O; Pacote consolidado pós-homologação, Prioridade 6) */}
      <Dialog open={!!pixModalCharge} onOpenChange={v => !v && setPixModalCharge(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>PIX — {pixModalCharge?.empresa_nome}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            {pixModalCharge?.pix_qr_code_base64 ? (
              <img src={`data:image/png;base64,${pixModalCharge.pix_qr_code_base64}`} alt="QR Code PIX" className="mx-auto w-48 h-48 border border-slate-200 rounded-md" />
            ) : (
              <p className="text-sm text-slate-500">QR Code indisponível — use o Copia e Cola abaixo.</p>
            )}
            <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1">
              <div><span className="text-slate-500">Empresa pagadora:</span> <span className="font-medium text-slate-900">{pixModalCharge?.empresa_nome}</span></div>
              <div><span className="text-slate-500">Competência:</span> <span className="font-medium text-slate-900">{pixModalCharge ? competenciaFromInternal(pixModalCharge.competencia) : "-"}</span></div>
              <div><span className="text-slate-500">Plano:</span> <span className="font-medium text-slate-900">{pixModalCharge?.plano_nome_snapshot || "-"}</span></div>
              <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{pixModalCharge ? money(pixModalCharge.valor_final) : "-"}</span></div>
              <div><span className="text-slate-500">Expira em:</span> <span className="font-medium text-slate-900">{fmtDateTimeBr(pixModalCharge?.pix_expiration)}</span></div>
              <div><span className="text-slate-500">Descrição:</span> <span className="font-medium text-slate-900">Mensalidade LOOTUS — Plano {pixModalCharge?.plano_nome_snapshot || ""} — Competência {pixModalCharge ? competenciaFromInternal(pixModalCharge.competencia) : ""}</span></div>
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 text-left">
              Antes de pagar, confira se os dados do recebedor exibidos pelo seu banco correspondem à empresa responsável pela cobrança.
            </div>
            {pixModalCharge?.pix_qr_code && (
              <Button variant="outline" onClick={() => copyToClipboard(pixModalCharge.pix_qr_code, "PIX Copia e Cola")} data-testid="pix-copiar-copia-cola"><Copy size={14} /> Copiar PIX Copia e Cola</Button>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPixModalCharge(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detalhes seguros de um evento do histórico (Pacote consolidado
          pós-homologação, Prioridade 9) — só os campos já whitelisted pelo
          backend em before/after (nunca token/CNPJ completo/e-mail/payload
          bruto — ver audit_platform em server.py). */}
      <Dialog open={!!historyDetailEvent} onOpenChange={v => !v && setHistoryDetailEvent(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{historyDetailEvent?.op_label}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-500">
              {historyDetailEvent?.empresa_nome} · {historyDetailEvent?.at ? new Date(historyDetailEvent.at).toLocaleString("pt-BR") : "-"} · {historyDetailEvent?.user_name || "Sistema"}
            </div>
            {historyDetailEvent?.before && Object.keys(historyDetailEvent.before).length > 0 && (
              <div>
                <div className="font-medium text-slate-900 mb-1">Antes</div>
                <div className="grid grid-cols-2 gap-1 p-2 rounded-md bg-slate-50 border border-slate-200">
                  {Object.entries(historyDetailEvent.before).map(([k, v]) => (
                    <div key={k} className="contents">
                      <div className="text-slate-500">{historyFieldLabel(k)}</div>
                      <div className="text-slate-900">{historyFieldValue(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {historyDetailEvent?.after && Object.keys(historyDetailEvent.after).length > 0 && (
              <div>
                <div className="font-medium text-slate-900 mb-1">Depois</div>
                <div className="grid grid-cols-2 gap-1 p-2 rounded-md bg-blue-50 border border-blue-200">
                  {Object.entries(historyDetailEvent.after).map(([k, v]) => (
                    <div key={k} className="contents">
                      <div className="text-slate-500">{historyFieldLabel(k)}</div>
                      <div className="text-slate-900">{historyFieldValue(v)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setHistoryDetailEvent(null)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação de substituição de instrumento ativo (item I) */}
      <Dialog open={!!instrumentConfirm} onOpenChange={v => !v && setInstrumentConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Substituir instrumento ativo?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-700">
            Já existe um {instrumentConfirm?.charge?.instrumento_tipo === "pix" ? "PIX" : "boleto"} aguardando pagamento para esta cobrança.
            Deseja substituí-lo por {instrumentConfirm?.tipo === "pix" ? "PIX" : "boleto"}? O instrumento anterior será cancelado com segurança
            (após confirmação junto ao Mercado Pago) e preservado no histórico.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInstrumentConfirm(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={confirmSubstituteInstrument} data-testid="instrumento-confirmar-substituicao">Substituir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
